/**
 * Custom JS for Broadcast Theme
 *
 * Keep custom scripts in this file for easier theme updates.
 * Ensure custom.js is imported in layout/theme.liquid.
 */

(function () {
  'use strict';

  // ==========================================================================
  // MOBILE IMAGE SLIDER WITH LOOPING FOR HOVER-IMAGES
  // Transform-based slider with clone looping, progress bar, and touch handling
  // ==========================================================================

  function enableGridSliderHoverImages() {
    // Only on mobile/touch
    if (window.innerWidth > 749) return;

    const gridSliderHoverImages = document.querySelectorAll(
      'hover-images .product-item__bg__slider'
    );

    gridSliderHoverImages.forEach(function (slider) {
      // Skip if already initialized
      if (slider.dataset.gridSliderHoverInit) return;

      // Skip if the parent grid-item has mobile slider disabled
      const gridItem = slider.closest('.product-item--no-mobile-slider');
      if (gridItem) return;

      // Skip if slider has zero width (hidden in inactive tab) - will init later when visible
      if (slider.clientWidth === 0) return;

      // Mark as initialized only after width check passes
      slider.dataset.gridSliderHoverInit = 'true';

      // Find slides
      let slides = slider.querySelectorAll('.product-item__bg__slide');
      if (slides.length === 0) {
        slides = slider.querySelectorAll('[data-hover-slide-touch]');
      }

      // Skip single-image products (no slider needed)
      if (slides.length < 2) return;

      // Style slides for transform-based sliding
      slides.forEach(function (slide) {
        slide.style.setProperty('min-width', '100%', 'important');
        slide.style.setProperty('flex', '0 0 100%', 'important');
      });

      // Set up progress bar CSS variables
      const hoverImages = slider.closest('hover-images');
      if (hoverImages && slides.length > 1) {
        const slidesCount = slides.length;
        hoverImages.style.setProperty('--slides-count', String(slidesCount), 'important');
        hoverImages.style.setProperty('--slider-index', '0', 'important');
      }

      // Add class to hover-images for CSS targeting
      if (hoverImages) {
        hoverImages.classList.add('hover-slider-init');
      }

      // Use CSS transforms for sliding instead of scroll — disable theme's native scroll-snap
      slider.style.setProperty('overflow', 'visible', 'important');
      slider.style.setProperty('scroll-snap-type', 'none', 'important');
      slider.style.setProperty('touch-action', 'pan-y', 'important'); // Allow vertical scroll

      const slidesCount = slides.length;

      // Clone first and last slides for seamless infinite looping
      // Layout: [clone-last] [real-0] [real-1] ... [real-n-1] [clone-first]
      const firstClone = slides[0].cloneNode(true);
      const lastClone = slides[slidesCount - 1].cloneNode(true);
      firstClone.setAttribute('aria-hidden', 'true');
      lastClone.setAttribute('aria-hidden', 'true');
      firstClone.style.setProperty('min-width', '100%', 'important');
      firstClone.style.setProperty('flex', '0 0 100%', 'important');
      lastClone.style.setProperty('min-width', '100%', 'important');
      lastClone.style.setProperty('flex', '0 0 100%', 'important');
      slider.appendChild(firstClone);
      slider.insertBefore(lastClone, slider.firstChild);

      let currentIndex = 0;
      let startX = 0;
      let startY = 0;
      let currentTranslate = 0;
      let startTranslate = 0;
      let isDragging = false;
      let direction = null;
      let isLooping = false;

      // Use Math.ceil to force integer pixel widths — eliminates subpixel gaps between slides
      function getSlideWidth() {
        if (hoverImages) {
          return Math.ceil(hoverImages.getBoundingClientRect().width);
        }
        return Math.ceil(slider.getBoundingClientRect().width);
      }

      // Set all slides (including clones) to integer pixel width
      function syncSlideWidths() {
        const w = getSlideWidth() + 'px';
        const allSlides = slider.children;
        for (let i = 0; i < allSlides.length; i++) {
          allSlides[i].style.setProperty('min-width', w, 'important');
          allSlides[i].style.setProperty('width', w, 'important');
          allSlides[i].style.setProperty('flex', '0 0 ' + w, 'important');
        }
      }

      // Real slide i is at flex position (i + 1) due to prepended clone
      function getTranslateForIndex(index) {
        return -(index + 1) * getSlideWidth();
      }

      function setSliderPosition(translate, animate) {
        slider.style.transition = animate ? 'transform 0.3s ease-out' : 'none';
        slider.style.transform = 'translateX(' + translate + 'px)';
      }

      function goToSlide(index, animate) {
        if (isLooping) return;

        if (index >= slidesCount) {
          // Forward loop: animate to clone-first, then jump to real-first
          isLooping = true;
          currentTranslate = getTranslateForIndex(slidesCount);
          setSliderPosition(currentTranslate, true);
          if (hoverImages) {
            hoverImages.style.setProperty('--slider-index', '0', 'important');
          }
          setTimeout(function () {
            currentIndex = 0;
            currentTranslate = getTranslateForIndex(0);
            setSliderPosition(currentTranslate, false);
            isLooping = false;
          }, 320);
          return;
        }

        if (index < 0) {
          // Backward loop: animate to clone-last, then jump to real-last
          isLooping = true;
          currentTranslate = getTranslateForIndex(-1);
          setSliderPosition(currentTranslate, true);
          if (hoverImages) {
            hoverImages.style.setProperty('--slider-index', String(slidesCount - 1), 'important');
          }
          setTimeout(function () {
            currentIndex = slidesCount - 1;
            currentTranslate = getTranslateForIndex(slidesCount - 1);
            setSliderPosition(currentTranslate, false);
            isLooping = false;
          }, 320);
          return;
        }

        currentIndex = index;
        currentTranslate = getTranslateForIndex(index);
        setSliderPosition(currentTranslate, animate);

        // Update progress bar
        if (hoverImages) {
          hoverImages.style.setProperty('--slider-index', String(currentIndex), 'important');
        }
      }

      // Force integer pixel widths on all slides to eliminate subpixel gaps
      syncSlideWidths();

      // Initialize position (skip past prepended clone)
      goToSlide(0, false);

      slider.addEventListener(
        'touchstart',
        function (e) {
          if (isLooping) return;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          startTranslate = currentTranslate;
          isDragging = true;
          direction = null;
          slider.style.transition = 'none';
        },
        { passive: true }
      );

      slider.addEventListener(
        'touchmove',
        function (e) {
          if (!isDragging) return;

          const currentX = e.touches[0].clientX;
          const currentY = e.touches[0].clientY;
          const deltaX = currentX - startX;
          const deltaY = currentY - startY;

          // Determine direction on first significant movement
          if (direction === null && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
            direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';

            if (direction === 'horizontal') {
              e.preventDefault();
            }
          }

          if (direction === 'horizontal') {
            e.preventDefault();
            e.stopPropagation();

            let newTranslate = startTranslate + deltaX;

            // Allow dragging into clone areas for seamless feel
            const maxTranslate = getTranslateForIndex(-1);
            const minTranslate = getTranslateForIndex(slidesCount);
            if (newTranslate > maxTranslate) {
              newTranslate = maxTranslate;
            } else if (newTranslate < minTranslate) {
              newTranslate = minTranslate;
            }

            currentTranslate = newTranslate;
            setSliderPosition(newTranslate, false);
          }
        },
        { passive: false }
      );

      slider.addEventListener(
        'touchend',
        function (e) {
          if (!isDragging) return;
          isDragging = false;

          if (direction === 'horizontal') {
            const deltaX = e.changedTouches[0].clientX - startX;
            const slideWidth = getSlideWidth();
            const threshold = slideWidth * 0.2;

            let targetIndex = currentIndex;

            if (deltaX < -threshold) {
              targetIndex = currentIndex + 1;
            } else if (deltaX > threshold) {
              targetIndex = currentIndex - 1;
            }

            goToSlide(targetIndex, true);
          }

          direction = null;
        },
        { passive: true }
      );

      slider.addEventListener(
        'touchcancel',
        function () {
          if (isDragging) {
            goToSlide(currentIndex, true);
          }
          isDragging = false;
          direction = null;
        },
        { passive: true }
      );
    });
  }

  // Helper to schedule multiple initialization attempts
  function scheduleInit(delays) {
    delays.forEach(function (delay) {
      setTimeout(enableGridSliderHoverImages, delay);
    });
  }

  // ==========================================================================
  // DESKTOP FLICKITY RE-INITIALIZATION
  // Triggers Flickity initialization for dynamically loaded hover-images on desktop
  // ==========================================================================

  function reinitDesktopFlickity(delays) {
    delays.forEach(function (delay) {
      setTimeout(function () {
        // Find hover-images inside grid-sliders
        const hoverImageElements = document.querySelectorAll('grid-slider hover-images');

        hoverImageElements.forEach(function (hoverImages) {
          // Skip if element has no dimensions (hidden)
          if (hoverImages.offsetWidth === 0) return;

          // Skip if Flickity is already properly initialized with working instance
          if (hoverImages.flkty && hoverImages.flkty.cells && hoverImages.flkty.cells.length > 0) {
            hoverImages.flkty.resize();
            return;
          }

          // Check for slider element
          const slider = hoverImages.querySelector('[data-hover-slider]');
          if (!slider) return;

          // Check if there are enough slides
          const slides = hoverImages.querySelectorAll('[data-hover-slide]');
          if (slides.length < 2) return;

          // If slider has flickity-enabled class but no working flkty instance, it's broken
          // Remove the class so we can reinitialize
          if (slider.classList.contains('flickity-enabled') && !hoverImages.flkty) {
            // Try to get and destroy existing broken Flickity
            const FlickityClass = window.Flickity || (window.theme && window.theme.Flickity);
            if (FlickityClass && FlickityClass.data) {
              const existingFlkty = FlickityClass.data(slider);
              if (existingFlkty) {
                try {
                  existingFlkty.destroy();
                } catch {
                  // Ignore destroy errors
                }
              }
            }
            slider.classList.remove('flickity-enabled');
          }

          // Fix: Set the slider property if it's missing
          if (!hoverImages.slider) {
            hoverImages.slider = slider;
          }

          // Try to call theme's initialization — guard against Flickity not yet loaded
          // Clarity reported "flkty" carousel errors; this ensures we only call
          // initFlickity when the Flickity class itself is available on the window.
          const FlickityAvailable = window.Flickity || (window.theme && window.theme.Flickity);
          if (typeof hoverImages.initFlickity === 'function' && FlickityAvailable) {
            try {
              hoverImages.initFlickity();
            } catch (e) {
              // Silently ignore init errors to prevent console noise and Clarity reporting
            }
          }
        });

        // Also trigger resize for any existing Flickity instances
        window.dispatchEvent(new Event('resize'));
      }, delay);
    });
  }

  // ==========================================================================
  // COLLECTION PAGE - TABLET IMAGE SLIDER WITH LOOPING + MOUSE DRAG
  // For touch-enabled tablets (750-1024px) where native scroll-snap
  // doesn't support mouse drag. Adds transform-based slider with
  // clone looping, progress bar, and both touch + mouse handling.
  // ==========================================================================

  function enableCollectionTabletSlider() {
    // Only on tablet-width touch devices on collection pages
    if (window.innerWidth < 750) return;
    if (!window.theme || !window.theme.touch) return;
    if (!document.body.classList.contains('template-collection')) return;

    const sliders = document.querySelectorAll('hover-images .product-item__bg__slider');

    sliders.forEach(function (slider) {
      // Skip if already initialized
      if (slider.dataset.tabletSliderInit) return;

      // Skip if inside a grid-slider/carousel
      if (slider.closest('grid-slider')) return;

      // Skip if slider has zero width (hidden) - will init later when visible
      if (slider.clientWidth === 0) return;

      slider.dataset.tabletSliderInit = 'true';

      let slides = slider.querySelectorAll('.product-item__bg__slide');
      if (slides.length === 0) {
        slides = slider.querySelectorAll('[data-hover-slide-touch]');
      }

      // Skip single-image products
      if (slides.length < 2) return;

      // Style slides for transform-based sliding
      slides.forEach(function (slide) {
        slide.style.setProperty('min-width', '100%', 'important');
        slide.style.setProperty('flex', '0 0 100%', 'important');
      });

      const hoverImages = slider.closest('hover-images');
      const slidesCount = slides.length;

      if (hoverImages && slidesCount > 1) {
        hoverImages.style.setProperty('--slides-count', String(slidesCount), 'important');
        hoverImages.style.setProperty('--slider-index', '0', 'important');
      }

      if (hoverImages) {
        hoverImages.classList.add('tablet-slider-init');
      }

      // Override scroll-snap with transform-based sliding
      slider.style.setProperty('overflow', 'visible', 'important');
      slider.style.setProperty('scroll-snap-type', 'none', 'important');
      slider.style.setProperty('touch-action', 'pan-y', 'important');

      // Clone first and last slides for seamless infinite looping
      const firstClone = slides[0].cloneNode(true);
      const lastClone = slides[slidesCount - 1].cloneNode(true);
      firstClone.setAttribute('aria-hidden', 'true');
      lastClone.setAttribute('aria-hidden', 'true');
      firstClone.style.setProperty('min-width', '100%', 'important');
      firstClone.style.setProperty('flex', '0 0 100%', 'important');
      lastClone.style.setProperty('min-width', '100%', 'important');
      lastClone.style.setProperty('flex', '0 0 100%', 'important');
      slider.appendChild(firstClone);
      slider.insertBefore(lastClone, slider.firstChild);

      let currentIndex = 0;
      let startX = 0;
      let startY = 0;
      let currentTranslate = 0;
      let startTranslate = 0;
      let isDragging = false;
      let direction = null;
      let isLooping = false;
      let dragMoved = false;

      // Use Math.ceil to force integer pixel widths — eliminates subpixel gaps between slides
      function getSlideWidth() {
        if (hoverImages) {
          return Math.ceil(hoverImages.getBoundingClientRect().width);
        }
        return Math.ceil(slider.getBoundingClientRect().width);
      }

      // Set all slides (including clones) to integer pixel width
      function syncSlideWidths() {
        const w = getSlideWidth() + 'px';
        const allSlides = slider.children;
        for (let i = 0; i < allSlides.length; i++) {
          allSlides[i].style.setProperty('min-width', w, 'important');
          allSlides[i].style.setProperty('width', w, 'important');
          allSlides[i].style.setProperty('flex', '0 0 ' + w, 'important');
        }
      }

      function getTranslateForIndex(index) {
        return -(index + 1) * getSlideWidth();
      }

      function setSliderPosition(translate, animate) {
        slider.style.transition = animate ? 'transform 0.3s ease-out' : 'none';
        slider.style.transform = 'translateX(' + translate + 'px)';
      }

      function goToSlide(index, animate) {
        if (isLooping) return;

        if (index >= slidesCount) {
          isLooping = true;
          currentTranslate = getTranslateForIndex(slidesCount);
          setSliderPosition(currentTranslate, true);
          if (hoverImages) {
            hoverImages.style.setProperty('--slider-index', '0', 'important');
          }
          setTimeout(function () {
            currentIndex = 0;
            currentTranslate = getTranslateForIndex(0);
            setSliderPosition(currentTranslate, false);
            isLooping = false;
          }, 320);
          return;
        }

        if (index < 0) {
          isLooping = true;
          currentTranslate = getTranslateForIndex(-1);
          setSliderPosition(currentTranslate, true);
          if (hoverImages) {
            hoverImages.style.setProperty('--slider-index', String(slidesCount - 1), 'important');
          }
          setTimeout(function () {
            currentIndex = slidesCount - 1;
            currentTranslate = getTranslateForIndex(slidesCount - 1);
            setSliderPosition(currentTranslate, false);
            isLooping = false;
          }, 320);
          return;
        }

        currentIndex = index;
        currentTranslate = getTranslateForIndex(index);
        setSliderPosition(currentTranslate, animate);

        if (hoverImages) {
          hoverImages.style.setProperty('--slider-index', String(currentIndex), 'important');
        }
      }

      // Force integer pixel widths on all slides to eliminate subpixel gaps
      syncSlideWidths();

      // Initialize position (skip past prepended clone)
      goToSlide(0, false);

      // Fix visibility issues caused by transform+overflow:hidden breaking
      // both lazy loading and AOS IntersectionObservers.
      slider.querySelectorAll('img').forEach(function (img) {
        // Force eager loading (lazy loading observer can't see through transforms)
        if (img.getAttribute('loading') === 'lazy') {
          img.setAttribute('loading', 'eager');
        }
        // Clear is-loading state (theme uses opacity: 0.000001 on is-loading imgs)
        if (img.complete) {
          img.classList.remove('is-loading');
          if (img.parentNode) img.parentNode.classList.remove('is-loading');
        } else {
          img.addEventListener(
            'load',
            function () {
              img.classList.remove('is-loading');
              if (img.parentNode) img.parentNode.classList.remove('is-loading');
            },
            { once: true }
          );
        }
      });

      // Mark AOS elements as already animated (AOS observer can't see through transforms)
      slider.querySelectorAll('[data-aos]').forEach(function (el) {
        el.classList.add('aos-animate');
      });

      // --- Touch events ---
      slider.addEventListener(
        'touchstart',
        function (e) {
          if (isLooping) return;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          startTranslate = currentTranslate;
          isDragging = true;
          direction = null;
          slider.style.transition = 'none';
        },
        { passive: true }
      );

      slider.addEventListener(
        'touchmove',
        function (e) {
          if (!isDragging) return;

          const currentX = e.touches[0].clientX;
          const currentY = e.touches[0].clientY;
          const deltaX = currentX - startX;
          const deltaY = currentY - startY;

          if (direction === null && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
            direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
            if (direction === 'horizontal') {
              e.preventDefault();
            }
          }

          if (direction === 'horizontal') {
            e.preventDefault();
            e.stopPropagation();

            let newTranslate = startTranslate + deltaX;
            const maxTranslate = getTranslateForIndex(-1);
            const minTranslate = getTranslateForIndex(slidesCount);
            if (newTranslate > maxTranslate) {
              newTranslate = maxTranslate;
            } else if (newTranslate < minTranslate) {
              newTranslate = minTranslate;
            }

            currentTranslate = newTranslate;
            setSliderPosition(newTranslate, false);
          }
        },
        { passive: false }
      );

      slider.addEventListener(
        'touchend',
        function (e) {
          if (!isDragging) return;
          isDragging = false;

          if (direction === 'horizontal') {
            const deltaX = e.changedTouches[0].clientX - startX;
            const slideWidth = getSlideWidth();
            const threshold = slideWidth * 0.2;

            let targetIndex = currentIndex;
            if (deltaX < -threshold) {
              targetIndex = currentIndex + 1;
            } else if (deltaX > threshold) {
              targetIndex = currentIndex - 1;
            }

            goToSlide(targetIndex, true);
          }

          direction = null;
        },
        { passive: true }
      );

      slider.addEventListener(
        'touchcancel',
        function () {
          if (isDragging) {
            goToSlide(currentIndex, true);
          }
          isDragging = false;
          direction = null;
        },
        { passive: true }
      );

      // --- Mouse events (for mouse/trackpad on touch-enabled tablets) ---
      slider.addEventListener('mousedown', function (e) {
        if (isLooping) return;
        e.preventDefault(); // Prevent browser image drag ghost
        startX = e.clientX;
        startY = e.clientY;
        startTranslate = currentTranslate;
        isDragging = true;
        direction = null;
        dragMoved = false;
        slider.style.transition = 'none';
      });

      slider.addEventListener('mousemove', function (e) {
        if (!isDragging) return;

        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;

        if (direction === null && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
          direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
        }

        if (direction === 'vertical') {
          isDragging = false;
          return;
        }

        if (direction === 'horizontal') {
          e.preventDefault();
          dragMoved = true;

          let newTranslate = startTranslate + deltaX;
          const maxTranslate = getTranslateForIndex(-1);
          const minTranslate = getTranslateForIndex(slidesCount);
          if (newTranslate > maxTranslate) {
            newTranslate = maxTranslate;
          } else if (newTranslate < minTranslate) {
            newTranslate = minTranslate;
          }

          currentTranslate = newTranslate;
          setSliderPosition(newTranslate, false);
        }
      });

      slider.addEventListener('mouseup', function (e) {
        if (!isDragging) return;
        isDragging = false;

        if (direction === 'horizontal') {
          const deltaX = e.clientX - startX;
          const slideWidth = getSlideWidth();
          const threshold = slideWidth * 0.2;

          let targetIndex = currentIndex;
          if (deltaX < -threshold) {
            targetIndex = currentIndex + 1;
          } else if (deltaX > threshold) {
            targetIndex = currentIndex - 1;
          }

          goToSlide(targetIndex, true);
        }

        direction = null;
      });

      slider.addEventListener('mouseleave', function () {
        if (!isDragging) return;
        isDragging = false;
        goToSlide(currentIndex, true);
        direction = null;
      });

      // Prevent product link navigation after mouse drag
      const productLink = slider.closest('a');
      if (productLink) {
        productLink.addEventListener(
          'click',
          function (e) {
            if (dragMoved) {
              e.preventDefault();
              e.stopPropagation();
              dragMoved = false;
            }
          },
          true
        );
      }
    });
  }

  function scheduleTabletInit(delays) {
    delays.forEach(function (delay) {
      setTimeout(enableCollectionTabletSlider, delay);
    });
  }

  // Initial load — use requestIdleCallback to avoid blocking user interactions (INP optimization)
  var _rIC = window.requestIdleCallback || function (cb) { setTimeout(cb, 50); };
  function deferredSliderInit() {
    _rIC(function () {
      scheduleInit([0, 500, 1000, 2000]);
      scheduleTabletInit([0, 500, 1000, 2000]);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', deferredSliderInit);
  } else {
    deferredSliderInit();
  }

  // Re-run when grid-sliders are added dynamically (related products, etc.)
  // Debounced to reduce main-thread blocking (INP optimization)
  let _sliderObserverTimer = null;
  const gridSliderHoverObserver = new MutationObserver(function (mutations) {
    if (_sliderObserverTimer) return;
    const shouldInit = mutations.some(function (mutation) {
      return Array.from(mutation.addedNodes).some(function (node) {
        if (node.nodeType !== 1) return false;
        return (
          (node.matches && (node.matches('grid-slider') || node.matches('hover-images'))) ||
          (node.querySelector &&
            (node.querySelector('grid-slider') || node.querySelector('hover-images')))
        );
      });
    });

    if (shouldInit) {
      _sliderObserverTimer = setTimeout(function () {
        _sliderObserverTimer = null;
        if (window.innerWidth <= 749) {
          scheduleInit([50, 200, 500]);
        } else {
          scheduleTabletInit([50, 200, 500]);
          reinitDesktopFlickity([50, 200, 500]);
        }
      }, 150);
    }
  });

  gridSliderHoverObserver.observe(document.body, { childList: true, subtree: true });

  // Listen for recently viewed products (capture phase since event doesn't bubble)
  document.addEventListener(
    'theme:recently-viewed:loaded',
    function () {
      // Mark recently viewed items to disable mobile slider (loaded dynamically without the flag)
      document.querySelectorAll('recently-viewed grid-item').forEach(function (item) {
        item.classList.add('product-item--no-mobile-slider');
      });

      if (window.innerWidth <= 749) {
        scheduleInit([50, 200, 500, 1000]);
      } else {
        scheduleTabletInit([100, 300, 600, 1000, 2000]);
        reinitDesktopFlickity([100, 300, 600, 1000, 2000]);
      }
    },
    true
  );

  // Tab-link slider init — uses event on the tab-link elements directly (avoids document-level handler)
  document.querySelectorAll('.tab-link').forEach(function (tab) {
    tab.addEventListener('click', function () {
      if (window.innerWidth <= 749) {
        scheduleInit([100, 300, 500, 1000]);
      } else {
        scheduleTabletInit([100, 300, 600, 1000, 2000]);
        reinitDesktopFlickity([100, 300, 600, 1000, 2000]);
      }
    });
  });

  // ==========================================================================
  // QUICK ADD MODAL - IMAGE SLIDER WITH PROGRESS BAR
  // Converts fading product images to a swipeable slider with looping
  // ==========================================================================

  function initQuickAddSlider(dialog) {
    if (!dialog) return;

    const productImages = dialog.querySelector('.product-quick-add__images');
    if (!productImages) return;

    const slider = productImages.querySelector('.product__slides');
    if (!slider) return;

    // Skip if already initialized
    if (productImages.dataset.quickAddSliderInit) return;

    const slides = slider.querySelectorAll('.product__slide');
    if (slides.length < 2) return;

    productImages.dataset.quickAddSliderInit = 'true';

    // Add class to activate CSS overrides
    productImages.classList.add('quick-add-slider-init');

    // Make all slides visible (remove theme's hidden/active classes)
    slides.forEach(function (slide) {
      slide.classList.remove('media--hidden', 'media--hiding');
      slide.classList.add('media--active');
    });

    const slidesCount = slides.length;

    // Clone first and last slides for seamless infinite looping
    // Layout: [clone-last] [real-0] [real-1] ... [real-n-1] [clone-first]
    const firstClone = slides[0].cloneNode(true);
    const lastClone = slides[slidesCount - 1].cloneNode(true);
    firstClone.setAttribute('aria-hidden', 'true');
    lastClone.setAttribute('aria-hidden', 'true');
    firstClone.classList.add('media--active');
    lastClone.classList.add('media--active');
    slider.appendChild(firstClone);
    slider.insertBefore(lastClone, slider.firstChild);

    // Set CSS variables for progress bar (uses real slide count)
    productImages.style.setProperty('--slides-count', String(slidesCount));
    productImages.style.setProperty('--slider-index', '0');

    // Slider state
    let currentIndex = 0; // Real index: 0 to slidesCount-1
    let startX = 0;
    let startY = 0;
    let currentTranslate = 0;
    let startTranslate = 0;
    let isDragging = false;
    let direction = null;
    let isLooping = false;

    function getSlideWidth() {
      return productImages.offsetWidth;
    }

    // Set all slides (including clones) to integer pixel width to eliminate subpixel gaps
    // Use querySelectorAll to avoid targeting non-slide children like .slider__arrows
    function syncSlideWidths() {
      const w = getSlideWidth() + 'px';
      const allSlides = slider.querySelectorAll('.product__slide');
      for (let i = 0; i < allSlides.length; i++) {
        allSlides[i].style.setProperty('min-width', w, 'important');
        allSlides[i].style.setProperty('width', w, 'important');
        allSlides[i].style.setProperty('flex', '0 0 ' + w, 'important');
      }
    }

    // Real slide i is at flex position (i + 1) due to prepended clone
    function getTranslateForIndex(index) {
      return -(index + 1) * getSlideWidth();
    }

    function setSliderPosition(translate, animate) {
      slider.style.transition = animate ? 'transform 0.3s ease-out' : 'none';
      slider.style.transform = 'translateX(' + translate + 'px)';
    }

    function goToSlide(index, animate) {
      if (isLooping) return; // Prevent rapid clicks during loop transition

      if (index >= slidesCount) {
        // Forward loop: animate to clone-first, then jump to real-first
        isLooping = true;
        currentTranslate = getTranslateForIndex(slidesCount);
        setSliderPosition(currentTranslate, true);
        productImages.style.setProperty('--slider-index', '0');
        setTimeout(function () {
          currentIndex = 0;
          currentTranslate = getTranslateForIndex(0);
          setSliderPosition(currentTranslate, false);
          isLooping = false;
        }, 320);
        return;
      }

      if (index < 0) {
        // Backward loop: animate to clone-last, then jump to real-last
        isLooping = true;
        currentTranslate = getTranslateForIndex(-1);
        setSliderPosition(currentTranslate, true);
        productImages.style.setProperty('--slider-index', String(slidesCount - 1));
        setTimeout(function () {
          currentIndex = slidesCount - 1;
          currentTranslate = getTranslateForIndex(slidesCount - 1);
          setSliderPosition(currentTranslate, false);
          isLooping = false;
        }, 320);
        return;
      }

      currentIndex = index;
      currentTranslate = getTranslateForIndex(index);
      setSliderPosition(currentTranslate, animate);
      productImages.style.setProperty('--slider-index', String(currentIndex));
    }

    // Force integer pixel widths on all slides to eliminate subpixel gaps
    syncSlideWidths();

    // Initialize position (skip past prepended clone)
    goToSlide(0, false);

    // Touch events
    slider.addEventListener(
      'touchstart',
      function (e) {
        if (isLooping) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        startTranslate = currentTranslate;
        isDragging = true;
        direction = null;
        slider.style.transition = 'none';
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchmove',
      function (e) {
        if (!isDragging) return;

        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const deltaX = currentX - startX;
        const deltaY = currentY - startY;

        // Determine direction on first significant movement
        if (direction === null && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
          direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
        }

        // Vertical: let page scroll
        if (direction === 'vertical') {
          isDragging = false;
          return;
        }

        if (direction === 'horizontal') {
          e.preventDefault();
          e.stopPropagation();

          let newTranslate = startTranslate + deltaX;

          // Allow dragging into clone areas for seamless feel
          const maxTranslate = getTranslateForIndex(-1);
          const minTranslate = getTranslateForIndex(slidesCount);
          if (newTranslate > maxTranslate) {
            newTranslate = maxTranslate;
          } else if (newTranslate < minTranslate) {
            newTranslate = minTranslate;
          }

          currentTranslate = newTranslate;
          setSliderPosition(newTranslate, false);
        }
      },
      { passive: false }
    );

    slider.addEventListener(
      'touchend',
      function (e) {
        if (!isDragging) return;
        isDragging = false;

        if (direction === 'horizontal') {
          const deltaX = e.changedTouches[0].clientX - startX;
          const slideWidth = getSlideWidth();
          const threshold = slideWidth * 0.2;

          let targetIndex = currentIndex;

          if (deltaX < -threshold) {
            targetIndex = currentIndex + 1;
          } else if (deltaX > threshold) {
            targetIndex = currentIndex - 1;
          }

          goToSlide(targetIndex, true);
        }

        direction = null;
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchcancel',
      function () {
        if (isDragging) {
          goToSlide(currentIndex, true);
        }
        isDragging = false;
        direction = null;
      },
      { passive: true }
    );

    // Override theme's product-images navigation to use our transform-based slider
    const productImagesEl = dialog.querySelector('product-images');
    if (productImagesEl) {
      // Remove theme's touch/mouse/drag handlers to prevent conflicts with our slider
      // (but keep arrows intact - don't call unbindEventListeners which removes arrows)
      const pml = productImagesEl.productMediaList || slider;
      if (pml) {
        pml.removeEventListener('mousedown', productImagesEl.handleMouseDown);
        pml.removeEventListener('mouseleave', productImagesEl.handleMouseLeave);
        pml.removeEventListener('mouseup', productImagesEl.handleMouseUp);
        pml.removeEventListener('mousemove', productImagesEl.handleMouseMove);
        pml.removeEventListener('touchstart', productImagesEl.handleMouseDown);
        pml.removeEventListener('touchend', productImagesEl.handleMouseUp);
        pml.removeEventListener('touchmove', productImagesEl.handleMouseMove);
        pml.removeEventListener('keyup', productImagesEl.handleKeyUp);
      }

      // Override navigation methods so arrows use our slider
      productImagesEl.showNextImage = function () {
        goToSlide(currentIndex + 1, true);
      };
      productImagesEl.showPreviousImage = function () {
        goToSlide(currentIndex - 1, true);
      };

      // Override setActiveMedia to prevent theme's fade behavior
      productImagesEl.setActiveMedia = function () {
        // No-op: we handle slide changes via goToSlide
      };

      // Move arrows out of .product__slides so they don't move with translateX
      // Arrows are already created by theme's connectedCallback -> bindEventListeners -> initArrows
      const arrows = slider.querySelector('.slider__arrows');
      if (arrows) {
        productImages.appendChild(arrows);
      }

      // Initialize position
      goToSlide(0, false);
    }
  }

  // Initialize when quick-add modal opens
  document.addEventListener('theme:quick-add:open', function () {
    setTimeout(function () {
      const dialog = document.querySelector('dialog.drawer.product-quick-add[open]');
      if (dialog) {
        initQuickAddSlider(dialog);
      }
    }, 100);
  });

  // Also watch for content loaded into quick-add dialogs via MutationObserver
  let _quickAddTimer = null;
  const quickAddObserver = new MutationObserver(function (mutations) {
    if (_quickAddTimer) return;
    let hasNewNodes = mutations.some(function (m) { return m.addedNodes.length > 0; });
    if (!hasNewNodes) return;
    _quickAddTimer = setTimeout(function () {
      _quickAddTimer = null;
      const dialog = document.querySelector('dialog.drawer.product-quick-add[open]');
      if (dialog) {
        initQuickAddSlider(dialog);
      }
    }, 150);
  });

  quickAddObserver.observe(document.body, { childList: true, subtree: true });

  // ==========================================================================
  // PRODUCT PAGE MOBILE SLIDER PROGRESS BAR
  // Adds progress bar indicator to product page image slider on mobile
  // ==========================================================================

  function initProductPageMobileSlider() {
    // Only run on product pages and mobile
    if (!document.body.classList.contains('template-product')) return;
    if (window.innerWidth > 749) return;

    const productImages = document.querySelector('.product__images--mobile-slider');
    if (!productImages) return;

    const slider = productImages.querySelector('.product__slides');
    if (!slider) return;

    // Don't re-initialize
    if (productImages.dataset.progressInit) return;
    productImages.dataset.progressInit = 'true';

    const slides = slider.querySelectorAll('.product__slide');
    const slidesCount = slides.length;

    if (slidesCount <= 1) return;

    // Activate transform-based slider CSS (disables native scroll)
    productImages.classList.add('product-slider-loop-init');

    // Make all slides visible
    slides.forEach(function (slide) {
      slide.classList.remove('media--hidden', 'media--hiding');
      slide.classList.add('media--active');
    });

    // Clone first and last slides for seamless infinite looping
    // Layout: [clone-last] [real-0] [real-1] ... [real-n-1] [clone-first]
    const firstClone = slides[0].cloneNode(true);
    const lastClone = slides[slidesCount - 1].cloneNode(true);
    firstClone.setAttribute('aria-hidden', 'true');
    lastClone.setAttribute('aria-hidden', 'true');
    slider.appendChild(firstClone);
    slider.insertBefore(lastClone, slider.firstChild);

    // Set CSS variables for progress bar
    productImages.style.setProperty('--slides-count', slidesCount);
    productImages.style.setProperty('--slider-progress', 0);

    // Slider state
    let currentIndex = 0;
    let startX = 0;
    let startY = 0;
    let currentTranslate = 0;
    let startTranslate = 0;
    let isDragging = false;
    let direction = null;
    let isLooping = false;

    function getSlideWidth() {
      return productImages.offsetWidth;
    }

    // Set all slides (including clones) to integer pixel width to eliminate subpixel gaps
    // Use querySelectorAll to avoid targeting non-slide children like .slider__arrows
    function syncSlideWidths() {
      const w = getSlideWidth() + 'px';
      const allSlides = slider.querySelectorAll('.product__slide');
      for (let i = 0; i < allSlides.length; i++) {
        allSlides[i].style.setProperty('min-width', w, 'important');
        allSlides[i].style.setProperty('width', w, 'important');
        allSlides[i].style.setProperty('flex', '0 0 ' + w, 'important');
      }
    }

    // Real slide i is at flex position (i + 1) due to prepended clone
    function getTranslateForIndex(index) {
      return -(index + 1) * getSlideWidth();
    }

    function setSliderPosition(translate, animate) {
      slider.style.transition = animate ? 'transform 0.3s ease-out' : 'none';
      slider.style.transform = 'translateX(' + translate + 'px)';
    }

    function goToSlide(index, animate) {
      if (isLooping) return;

      if (index >= slidesCount) {
        // Forward loop: animate to clone-first, then jump to real-first
        isLooping = true;
        currentTranslate = getTranslateForIndex(slidesCount);
        setSliderPosition(currentTranslate, true);
        productImages.style.setProperty('--slider-progress', 0);
        setTimeout(function () {
          currentIndex = 0;
          currentTranslate = getTranslateForIndex(0);
          setSliderPosition(currentTranslate, false);
          isLooping = false;
        }, 320);
        return;
      }

      if (index < 0) {
        // Backward loop: animate to clone-last, then jump to real-last
        isLooping = true;
        currentTranslate = getTranslateForIndex(-1);
        setSliderPosition(currentTranslate, true);
        productImages.style.setProperty('--slider-progress', slidesCount - 1);
        setTimeout(function () {
          currentIndex = slidesCount - 1;
          currentTranslate = getTranslateForIndex(slidesCount - 1);
          setSliderPosition(currentTranslate, false);
          isLooping = false;
        }, 320);
        return;
      }

      currentIndex = index;
      currentTranslate = getTranslateForIndex(index);
      setSliderPosition(currentTranslate, animate);
      productImages.style.setProperty('--slider-progress', currentIndex);
    }

    // Force integer pixel widths on all slides to eliminate subpixel gaps
    syncSlideWidths();

    // Initialize position (skip past prepended clone)
    goToSlide(0, false);

    // Touch events
    slider.addEventListener(
      'touchstart',
      function (e) {
        if (isLooping) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        startTranslate = currentTranslate;
        isDragging = true;
        direction = null;
        slider.style.transition = 'none';
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchmove',
      function (e) {
        if (!isDragging) return;

        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const deltaX = currentX - startX;
        const deltaY = currentY - startY;

        if (direction === null && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
          direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
        }

        if (direction === 'vertical') {
          isDragging = false;
          return;
        }

        if (direction === 'horizontal') {
          e.preventDefault();
          e.stopPropagation();

          let newTranslate = startTranslate + deltaX;

          // Allow dragging into clone areas for seamless feel
          const maxTranslate = getTranslateForIndex(-1);
          const minTranslate = getTranslateForIndex(slidesCount);
          if (newTranslate > maxTranslate) {
            newTranslate = maxTranslate;
          } else if (newTranslate < minTranslate) {
            newTranslate = minTranslate;
          }

          currentTranslate = newTranslate;
          setSliderPosition(newTranslate, false);
        }
      },
      { passive: false }
    );

    slider.addEventListener(
      'touchend',
      function (e) {
        if (!isDragging) return;
        isDragging = false;

        if (direction === 'horizontal') {
          const deltaX = e.changedTouches[0].clientX - startX;
          const slideWidth = getSlideWidth();
          const threshold = slideWidth * 0.2;

          let targetIndex = currentIndex;

          if (deltaX < -threshold) {
            targetIndex = currentIndex + 1;
          } else if (deltaX > threshold) {
            targetIndex = currentIndex - 1;
          }

          goToSlide(targetIndex, true);
        }

        direction = null;
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchcancel',
      function () {
        if (isDragging) {
          goToSlide(currentIndex, true);
        }
        isDragging = false;
        direction = null;
      },
      { passive: true }
    );

    // Override theme's product-images navigation for variant selection
    let productImagesEl = productImages.closest('product-images');
    if (!productImagesEl) productImagesEl = productImages;

    if (productImagesEl && productImagesEl.tagName === 'PRODUCT-IMAGES') {
      const originalSlides = Array.from(slides);

      productImagesEl.setActiveMedia = function (mediaId) {
        if (!mediaId) return;
        for (let i = 0; i < originalSlides.length; i++) {
          if (originalSlides[i].getAttribute('data-media-id') === String(mediaId)) {
            goToSlide(i, true);
            return;
          }
        }
      };

      productImagesEl.showNextImage = function () {
        goToSlide(currentIndex + 1, true);
      };

      productImagesEl.showPreviousImage = function () {
        goToSlide(currentIndex - 1, true);
      };
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      (window.requestIdleCallback || function(cb){setTimeout(cb,50)})(initProductPageMobileSlider);
    });
  } else {
    (window.requestIdleCallback || function(cb){setTimeout(cb,50)})(initProductPageMobileSlider);
  }

  // Re-initialize on resize (in case user switches breakpoints)
  let resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (window.innerWidth <= 749) {
        initProductPageMobileSlider();
      } else {
        enableCollectionTabletSlider();
      }
    }, 250);
  });

  // ==========================================================================
  // VARIANT PICKER CUSTOMIZATIONS (PRODUCT PAGE ONLY)
  // - Removes default size/length pre-selection on page load
  // - Shows validation error when adding to cart without selecting size
  // ==========================================================================

  let validationListenerAdded = false;

  function hasVariantInUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('variant') && urlParams.get('variant');
  }

  function getSizeFieldsets(variantSelect) {
    const allFieldsets = variantSelect.querySelectorAll('.selector-wrapper');
    return Array.from(allFieldsets).filter(function (fieldset) {
      if (fieldset.classList.contains('selector-wrapper--size')) return true;
      const optionName = fieldset.querySelector('.radio__legend__option-name');
      if (optionName) {
        const name = optionName.textContent.toLowerCase().trim();
        return name === 'length' || name === 'size' || name === 'taille' || name === 'longueur';
      }
      return false;
    });
  }

  function forceHeaderHiddenDuringValidation() {
    const header = document.querySelector('[data-header-sticky]');
    if (!header) return;

    header.classList.add('header-scroll-hide');
    header.dataset.lockedByValidation = 'true';
  }

  function initProductPageVariantPicker() {
    // Only run on product pages
    if (!document.body.classList.contains('template-product')) return;

    const variantSelect = document.querySelector('product-info[id^="MainProduct"] variant-selects');
    if (!variantSelect) return;

    const variantInUrl = hasVariantInUrl();
    const alreadyProcessed = variantSelect.dataset.customInit === 'true';

    // Skip if already processed with URL variant
    if (alreadyProcessed && variantInUrl) return;

    const shouldResetSelection = !variantInUrl && !alreadyProcessed;
    variantSelect.dataset.customInit = 'true';

    const sizeFieldsets = getSizeFieldsets(variantSelect);

    // Reset selection (no pre-selection) only on initial load without URL variant
    if (shouldResetSelection) {
      sizeFieldsets.forEach(function (fieldset) {
        const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
        const selectedValueDisplay = fieldset.querySelector('[data-selected-value]');

        radioButtons.forEach(function (radio) {
          radio.checked = false;
        });

        if (selectedValueDisplay) {
          selectedValueDisplay.textContent = '';
        }
      });
    }

    // Add change listeners to update selected value display on product page
    sizeFieldsets.forEach(function (fieldset) {
      const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
      radioButtons.forEach(function (radio) {
        if (radio.dataset.productPageChangeListener) return;
        radio.dataset.productPageChangeListener = 'true';

        radio.addEventListener('change', function () {
          if (radio.checked) {
            const selectedValueDisplay = fieldset.querySelector('[data-selected-value]');
            if (selectedValueDisplay) {
              const value = radio.value
                .replace(/ Inches/g, '"')
                .replace(/ inches/g, '"')
                .replace(/ Inch/g, '"')
                .replace(/ inch/g, '"');
              selectedValueDisplay.textContent = value;
            }
          }
        });
      });
    });

    // Add variant selection handler for product page
    const productInfo = variantSelect.closest('product-info');
    if (productInfo && !variantSelect.dataset.variantChangeHandler) {
      variantSelect.dataset.variantChangeHandler = 'true';

      variantSelect.addEventListener('change', async function () {
        // Get selected option value IDs
        const selectedRadios = variantSelect.querySelectorAll('fieldset input:checked');
        const selectedOptionValueIds = Array.from(selectedRadios)
          .map((radio) => radio.dataset.optionValueId)
          .filter(Boolean);

        if (selectedOptionValueIds.length === 0) return;

        // Get product URL and section ID
        const productUrl = productInfo.dataset.url || window.location.pathname;
        const sectionId = productInfo.dataset.section;
        if (!sectionId) return;

        // Build request URL
        const params = new URLSearchParams();
        selectedOptionValueIds.forEach((id) => params.append('option_values[]', id));
        params.append('section_id', sectionId);
        const requestUrl = `${productUrl}?${params.toString()}`;

        try {
          const response = await fetch(requestUrl);
          if (!response.ok) return;
          const html = await response.text();

          // Parse response
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Get variant from response
          const variantScript = doc.querySelector('script[data-selected-variant]');
          if (!variantScript || !variantScript.textContent) return;

          const variant = JSON.parse(variantScript.textContent);
          if (!variant || !variant.id) return;

          // Update variant script in DOM
          const currentScript = variantSelect.querySelector('script[data-selected-variant]');
          if (currentScript) {
            currentScript.textContent = variantScript.textContent;
          }

          // Update hidden form input
          const form = productInfo.querySelector('form[data-product-form]');
          if (form) {
            const variantInput = form.querySelector('input[name="id"]');
            if (variantInput) {
              variantInput.value = variant.id;
              variantInput.disabled = false;
            }
          }

          // Update URL
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());

          // Update price display
          const priceSource = doc.querySelector('.product__price');
          const priceTarget = productInfo.querySelector('.product__price');
          if (priceSource && priceTarget) {
            priceTarget.innerHTML = priceSource.innerHTML;
          }

          // Update add to cart button
          const buttonSource = doc.querySelector('[data-add-to-cart]');
          const buttonTarget = productInfo.querySelector('[data-add-to-cart]');
          if (buttonSource && buttonTarget) {
            buttonTarget.disabled = buttonSource.disabled;
            const textSource = buttonSource.querySelector('.btn__text');
            const textTarget = buttonTarget.querySelector('.btn__text');
            if (textSource && textTarget) {
              textTarget.textContent = textSource.textContent;
            }
          }

          // Update preorder input
          const preorderInput = productInfo.querySelector('[data-product-preorder]');
          if (preorderInput) {
            const isPreorder = buttonSource?.dataset?.preorder === 'true';
            preorderInput.disabled = !isPreorder;
          }
        } catch {
          // Fetch or parse error, ignore
        }
      });
    }

    // Add-to-cart validation (capture phase, product page only)
    if (!validationListenerAdded) {
      validationListenerAdded = true;

      document.addEventListener(
        'click',
        function (event) {
          const button = event.target.closest('[data-add-to-cart], [type="submit"][name="add"]');
          if (!button) return;

          const form = button.form || button.closest('form');
          if (!form) return;

          // Only validate on main product page, not quick add
          const productInfo = form.closest('product-info[id^="MainProduct"]');
          if (!productInfo) return;

          const variantSelect = productInfo.querySelector('variant-selects');
          if (!variantSelect) return;

          const sizeFieldsets = getSizeFieldsets(variantSelect);
          if (sizeFieldsets.length === 0) return;

          let hasError = false;

          sizeFieldsets.forEach(function (fieldset) {
            const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
            const isAnyChecked = Array.from(radioButtons).some(function (radio) {
              return radio.checked;
            });

            if (!isAnyChecked) {
              hasError = true;

              // Add error container dynamically if it doesn't exist
              let errorMsg = fieldset.querySelector('.variant-error-message');
              if (!errorMsg) {
                const radioButtonsContainer = fieldset.querySelector('.radio__buttons');
                if (radioButtonsContainer) {
                  errorMsg = document.createElement('div');
                  errorMsg.className = 'variant-error-message';
                  errorMsg.textContent = 'Please choose a size';
                  radioButtonsContainer.parentNode.insertBefore(
                    errorMsg,
                    radioButtonsContainer.nextSibling
                  );

                  // Add change listeners to hide error (guard against duplicates on section reload)
                  radioButtons.forEach(function (radio) {
                    if (radio.dataset.variantErrorListener) return;
                    radio.dataset.variantErrorListener = '1';
                    radio.addEventListener('change', function () {
                      const err = fieldset.querySelector('.variant-error-message');
                      if (err) err.style.display = 'none';
                      fieldset.classList.remove('variant-error');
                    });
                  });
                }
              }

              if (errorMsg) {
                errorMsg.style.display = 'block';
              }
              fieldset.classList.add('variant-error');
            }
          });

          if (hasError) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            const firstError = variantSelect.querySelector('.selector-wrapper.variant-error');
            if (firstError) {
              forceHeaderHiddenDuringValidation();

              firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });

              // keep header hidden during smooth scroll
              setTimeout(() => {
                const header = document.querySelector('[data-header-sticky]');
                if (header && header.dataset.lockedByValidation === 'true') {
                  header.classList.add('header-scroll-hide');
                }
              }, 300);
            }

            return false;
          }

          // Safeguard: Ensure variant input has the correct value before submission
          const variantInput = form.querySelector('input[name="id"]');
          const variantScript = variantSelect.querySelector('script[data-selected-variant]');

          if (variantInput && variantScript && variantScript.textContent) {
            try {
              const selectedVariant = JSON.parse(variantScript.textContent);
              if (selectedVariant && selectedVariant.id) {
                if (variantInput.value !== String(selectedVariant.id)) {
                  variantInput.value = selectedVariant.id;
                }
                variantInput.disabled = false;
              }
            } catch {
              // Script content is not valid JSON, ignore
            }
          }
        },
        true
      );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      (window.requestIdleCallback || function(cb){setTimeout(cb,50)})(initProductPageVariantPicker);
    });
  } else {
    (window.requestIdleCallback || function(cb){setTimeout(cb,50)})(initProductPageVariantPicker);
  }

  // ==========================================================================
  // QUICK ADD DRAWER - RESET PRE-SELECTION & VALIDATION
  // Clears default variant selection when drawer opens with new product content
  // Shows error message if user tries to add without selecting size
  // ==========================================================================

  function resetQuickAddVariantSelection(drawer) {
    if (!drawer) return;

    const variantSelect = drawer.querySelector('variant-selects');
    if (!variantSelect) return;

    const sizeFieldsets = getSizeFieldsets(variantSelect);
    if (sizeFieldsets.length === 0) return; // No size/length options, skip reset

    sizeFieldsets.forEach(function (fieldset) {
      const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
      const selectedValueDisplay = fieldset.querySelector('[data-selected-value]');

      // Uncheck all radio buttons
      radioButtons.forEach(function (radio) {
        radio.checked = false;
        radio.removeAttribute('checked');
      });

      // Clear selected value display
      if (selectedValueDisplay) {
        selectedValueDisplay.textContent = '';
      }

      // Add error message container if not exists
      const radioButtonsContainer = fieldset.querySelector('.radio__buttons');
      if (radioButtonsContainer && !fieldset.querySelector('.variant-error-message')) {
        const errorContainer = document.createElement('div');
        errorContainer.className = 'variant-error-message';
        errorContainer.textContent = 'Please choose a size';
        radioButtonsContainer.parentNode.insertBefore(
          errorContainer,
          radioButtonsContainer.nextSibling
        );
      }

      // Add change listener to hide error on selection and update display
      radioButtons.forEach(function (radio) {
        if (radio.dataset.quickAddChangeListener) return;
        radio.dataset.quickAddChangeListener = 'true';

        radio.addEventListener('change', function () {
          const errorMsg = fieldset.querySelector('.variant-error-message');
          if (errorMsg) {
            errorMsg.style.display = 'none';
          }
          fieldset.classList.remove('variant-error');

          // Update the selected value display text
          if (radio.checked) {
            const selectedValueDisplay = fieldset.querySelector('[data-selected-value]');
            if (selectedValueDisplay) {
              const value = radio.value
                .replace(/ Inches/g, '"')
                .replace(/ inches/g, '"')
                .replace(/ Inch/g, '"')
                .replace(/ inch/g, '"');
              selectedValueDisplay.textContent = value;
            }
          }
        });
      });
    });

    // Clear the selected variant script data
    const variantScript = variantSelect.querySelector('script[data-selected-variant]');
    if (variantScript) {
      variantScript.textContent = '';
    }

    // Clear the hidden variant ID input in the form
    const productInfo = drawer.querySelector('product-info');
    if (productInfo) {
      const formId = 'product-form-' + productInfo.dataset.section;
      const form = document.getElementById(formId);
      if (form) {
        const variantInput = form.querySelector('input[name="id"].product-variant-id');
        if (variantInput) {
          variantInput.value = '';
        }
      }

      // Disable the preorder input until a variant is selected
      const preorderInput = productInfo.querySelector('[data-product-preorder]');
      if (preorderInput) {
        preorderInput.disabled = true;
      }
    }
  }

  // Listen for quick add modal open event
  document.addEventListener('theme:quick-add:open', function () {
    const drawer = document.querySelector('dialog.drawer.product-quick-add[open]');
    if (drawer) {
      resetQuickAddVariantSelection(drawer);
    }
  });

  // Quick add drawer validation (capture phase)
  let quickAddValidationAdded = false;
  if (!quickAddValidationAdded) {
    quickAddValidationAdded = true;

    document.addEventListener(
      'click',
      function (event) {
        const button = event.target.closest('[data-add-to-cart], [type="submit"][name="add"]');
        if (!button) return;

        // Only handle quick add drawer, not product page
        const drawer = button.closest('dialog.drawer.product-quick-add');
        if (!drawer) return;

        const form = button.form || button.closest('form');
        if (!form) return;

        const productInfo = form.closest('product-info');
        if (!productInfo) return;

        const variantSelect = productInfo.querySelector('variant-selects');
        if (!variantSelect) return;

        const sizeFieldsets = getSizeFieldsets(variantSelect);
        if (sizeFieldsets.length === 0) return;

        let hasError = false;

        sizeFieldsets.forEach(function (fieldset) {
          const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
          const isAnyChecked = Array.from(radioButtons).some(function (radio) {
            return radio.checked;
          });

          if (!isAnyChecked) {
            hasError = true;
            const errorMsg = fieldset.querySelector('.variant-error-message');
            if (errorMsg) {
              errorMsg.style.display = 'block';
            }
            fieldset.classList.add('variant-error');
          }
        });

        if (hasError) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          const firstError = variantSelect.querySelector('.selector-wrapper.variant-error');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }

          return false;
        }

        // Safeguard: Ensure variant input has the correct value before submission
        const variantInput = form.querySelector('input[name="id"]');
        const variantScript = variantSelect.querySelector('script[data-selected-variant]');

        if (variantInput && variantScript && variantScript.textContent) {
          try {
            const selectedVariant = JSON.parse(variantScript.textContent);
            if (selectedVariant && selectedVariant.id) {
              if (variantInput.value !== String(selectedVariant.id)) {
                variantInput.value = selectedVariant.id;
              }
              variantInput.disabled = false;
            }
          } catch {
            // Script content is not valid JSON, ignore
          }
        }
      },
      true
    );
  }

  // ==========================================================================
  // STICKY CART BAR ADD TO CART FIX
  // Fixes the add to cart button for products with only a default variant
  // The original cart-bar.js dispatches a click event which doesn't properly
  // trigger form submission. This fix intercepts the click and dispatches
  // theme:cart:add with a proxy button that references the product form.
  // ==========================================================================

  document.addEventListener(
    'click',
    function (event) {
      const cartBarButton = event.target.closest('[data-cart-bar-add-to-cart]');
      if (!cartBarButton) return;
      if (cartBarButton.hasAttribute('disabled')) return;

      const cartBar = cartBarButton.closest('cart-bar');
      if (!cartBar) return;

      const productInfo = cartBar.closest('product-info');
      if (!productInfo) return;

      const form = productInfo.querySelector('[data-product-form]');
      if (!form) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const header = document.querySelector('[data-header-sticky]');

      if (header) {
        header.classList.add('header-scroll-hide');
        header.dataset.lockedByValidation = 'true';
      }

      // Prevent focus-based scroll
      document.activeElement?.blur();

      cartBarButton.classList.add('is-loading');
      cartBarButton.setAttribute('disabled', 'disabled');

      const formSubmitButton = form.querySelector('[data-add-to-cart]');
      if (!formSubmitButton) return;

      document.dispatchEvent(
        new CustomEvent('theme:cart:add', {
          detail: { button: formSubmitButton },
          bubbles: false,
        })
      );
    },
    true
  );

  let validationScrollTimeout = null;
  window.addEventListener(
    'scroll',
    function () {
      const header = document.querySelector('[data-header-sticky]');
      if (!header) return;

      if (header.dataset.lockedByValidation === 'true') {
        // Debounce: only clear lock after scroll has stopped for 600ms
        clearTimeout(validationScrollTimeout);
        validationScrollTimeout = setTimeout(function () {
          delete header.dataset.lockedByValidation;
        }, 600);
      }
    },
    { passive: true }
  );

  const chatObserver = new MutationObserver(() => {
    const target = document.querySelector('.woot-widget-holder');

    if (target) {
      chatObserver.disconnect(); // Stop watching entire body once found

      if (target.style.zIndex !== '5598') {
        target.style.setProperty('z-index', '5598', 'important');
      }

      const localObserver = new MutationObserver(() => {
        if (target.style.zIndex !== '5598') {
          target.style.setProperty('z-index', '5598', 'important');
        }
      });

      localObserver.observe(target, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }
  });

  chatObserver.observe(document.body, { childList: true, subtree: false });
})();

// ==========================================================================
// TIERED CART OFFERS
// Auto-applies discount codes and free products based on cart subtotal tiers.
// Reads configuration from data attributes rendered by tiered-offers.liquid.
// ==========================================================================
(function initTieredOffers() {
  'use strict';

  let isProcessing = false;
  let tieredOffersEl = null;
  let tiers = [];
  let cartErrorTimer = null;
  let previousSubtotal = null;

  // --- Show a toast error appended to body (survives cart DOM rebuilds), auto-dismiss after 5 s ---
  function showCartError(message) {
    let toast = document.getElementById('tiered-offers-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'tiered-offers-toast';
      toast.className = 'tiered-offers__toast';
      document.body.appendChild(toast);
    }
    toast.innerText = message;
    toast.classList.add('is-visible');
    clearTimeout(cartErrorTimer);
    cartErrorTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 5000);
  }

  // --- Read tier config from DOM data attributes ---
  function readConfig() {
    tieredOffersEl = document.querySelector('[data-tiered-offers]');
    if (!tieredOffersEl) {
      return false;
    }

    const currencyRate =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    tiers = [];

    for (let i = 1; i <= 3; i++) {
      const thresholdRaw =
        Number(tieredOffersEl.getAttribute('data-tier-' + i + '-threshold')) || 0;
      const variantId = tieredOffersEl.getAttribute('data-tier-' + i + '-variant-id') || '';
      const rewardType = tieredOffersEl.getAttribute('data-tier-' + i + '-reward-type') || '';

      if (thresholdRaw <= 0) continue;

      tiers.push({
        tier: i,
        threshold: thresholdRaw * 100 * currencyRate, // cents in presentment currency
        thresholdRaw: thresholdRaw,
        label: tieredOffersEl.getAttribute('data-tier-' + i + '-label') || '',
        rewardType: rewardType,
        discountCode: tieredOffersEl.getAttribute('data-tier-' + i + '-discount-code') || '',
        variantId: variantId,
        maxQty: Number(tieredOffersEl.getAttribute('data-tier-' + i + '-max-qty')) || 1,
      });
    }

    tiers.sort(function (a, b) {
      return a.threshold - b.threshold;
    });
    return tiers.length > 0;
  }

  // --- Read subtotal from DOM (no network call) ---
  // Uses total_price (post-discount) so free gifts only qualify after all
  // discounts are applied. Safe because gift items are $0 and don't affect totals.
  function getSubtotalFromDOM() {
    const el = document.querySelector('[data-cart-total]');
    return el ? parseInt(el.getAttribute('data-cart-total'), 10) || 0 : null;
  }

  // --- Find gift items in DOM by scanning cart item variant IDs ---
  // Cart items have data-item="{variant_id}:{hash}"
  function findGiftItemsInDOM() {
    const gifts = {};
    tiers.forEach(function (tier) {
      if (tier.rewardType !== 'free_product' || !tier.variantId) return;
      const itemEl = document.querySelector('[data-item^="' + tier.variantId + ':"]');
      if (itemEl) {
        gifts[tier.tier] = {
          key: itemEl.getAttribute('data-item'),
        };
      }
    });
    return gifts;
  }

  // --- Cart AJAX helpers ---
  function fetchCartJson() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then(function (r) {
      return r.json();
    });
  }

  function addItemToCart(variantId, tierNumber) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: { _offer_tier: 'tier_' + tierNumber },
          },
        ],
      }),
    }).then(function (r) {
      return r.json();
    });
  }

  function removeItemFromCart(lineItemKey) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity: 0 }),
    }).then(function (r) {
      return r.json();
    });
  }

  function updateCartDiscount(discountString) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: discountString }),
    }).then(function (r) {
      return r.json();
    });
  }

  // --- Find gift items in cart by _offer_tier property ---
  function findGiftItems(cartData) {
    const gifts = {};
    if (!cartData || !cartData.items) return gifts;
    cartData.items.forEach(function (item) {
      if (item.properties && item.properties._offer_tier) {
        const match = item.properties._offer_tier.match(/tier_(\d+)/);
        if (match) {
          gifts[parseInt(match[1], 10)] = {
            key: item.key,
            variantId: item.variant_id,
          };
        }
      }
    });
    return gifts;
  }

  // --- Get the highest qualifying discount code ---
  function getHighestQualifyingDiscountCode(effectiveSubtotal) {
    let code = '';
    tiers.forEach(function (tier) {
      if (
        tier.rewardType === 'discount_code' &&
        tier.discountCode &&
        effectiveSubtotal >= tier.threshold
      ) {
        code = tier.discountCode;
      }
    });
    return code;
  }

  // --- Core: evaluate tiers and apply/remove rewards ---
  async function evaluateAndApply(isUserAction) {
    if (isProcessing) return;
    isProcessing = true;

    try {
      // Read subtotal from DOM (instant, no network call)
      // Gift products are $0 so data-cart-total equals effective subtotal
      let effectiveSubtotal = getSubtotalFromDOM();

      // Fallback to /cart.js only if DOM isn't ready (e.g. initial page load)
      let cartData = null;
      if (effectiveSubtotal === null) {
        cartData = await fetchCartJson();
        if (cartData) {
          // Use total_price (post-discount) to match DOM behavior
          effectiveSubtotal = cartData.total_price || 0;
          // Subtract any gift items that have a price (shouldn't happen, but defensive)
          if (cartData.items) {
            cartData.items.forEach(function (item) {
              const isOfferGift = item.properties && item.properties._offer_tier;
              if (isOfferGift) {
                effectiveSubtotal -= item.final_line_price || 0;
              }
            });
          }
        } else {
          effectiveSubtotal = 0;
        }
      }

      // Update UI immediately
      updateUI(effectiveSubtotal);

      // Find existing gifts from DOM (instant) or from fetched cart data
      const existingGifts = cartData ? findGiftItems(cartData) : findGiftItemsInDOM();
      const cartOps = [];
      let cartChanged = false;

      // --- Free product tiers ---
      for (let t = 0; t < tiers.length; t++) {
        const tier = tiers[t];
        if (tier.rewardType !== 'free_product' || !tier.variantId) continue;

        const isQualified = effectiveSubtotal >= tier.threshold;
        const existingGift = existingGifts[tier.tier];
        const declinedKey = 'tiered_offer_declined_' + tier.tier;
        const activeKey = 'tiered_offer_active_' + tier.tier;
        const unavailableKey = 'tiered_offer_unavailable_' + tier.tier;
        const wasDeclined = sessionStorage.getItem(declinedKey) === 'true';
        const wasActive = sessionStorage.getItem(activeKey) === 'true';
        const unavailableMsg = sessionStorage.getItem(unavailableKey);
        const wasUnavailable = unavailableMsg !== null;

        if (isQualified && existingGift) {
          sessionStorage.setItem(activeKey, 'true');
        } else if (isQualified && !existingGift && wasActive && !wasDeclined) {
          sessionStorage.setItem(declinedKey, 'true');
        } else if (isQualified && !existingGift && !wasActive && !wasDeclined && !wasUnavailable) {
          cartOps.push(
            addItemToCart(tier.variantId, tier.tier).then(function (addResult) {
              if (!addResult.status) {
                sessionStorage.setItem(activeKey, 'true');
                cartChanged = true;
              } else {
                const msg = addResult.description || addResult.message || 'Free gift is currently unavailable.';
                sessionStorage.setItem(unavailableKey, msg);
                showCartError(msg);
              }
            }).catch(function () {
              const msg = 'Free gift could not be added. Please try again later.';
              sessionStorage.setItem(unavailableKey, msg);
              showCartError(msg);
            })
          );
        } else if (isQualified && !existingGift && wasUnavailable) {
          const justCrossed = previousSubtotal === null || previousSubtotal < tier.threshold;
          if (isUserAction && justCrossed) showCartError(unavailableMsg);
        } else if (!isQualified && existingGift) {
          cartOps.push(removeItemFromCart(existingGift.key));
          cartChanged = true;
          sessionStorage.removeItem(activeKey);
          sessionStorage.removeItem(declinedKey);
          sessionStorage.removeItem(unavailableKey);
        } else if (!isQualified && !existingGift) {
          sessionStorage.removeItem(activeKey);
          sessionStorage.removeItem(declinedKey);
          sessionStorage.removeItem(unavailableKey);
        }
      }

      // --- Run free product operations immediately, refresh as soon as they complete ---
      previousSubtotal = effectiveSubtotal;

      if (cartOps.length > 0) {
        await Promise.all(cartOps);
        if (cartChanged) {
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        }
      }

      isProcessing = false;

      // --- Discount code tiers run separately after free product ops (needs /cart.js) ---
      const hasDiscountTiers = tiers.some(function (t) {
        return t.rewardType === 'discount_code' && t.discountCode;
      });

      if (hasDiscountTiers) {
        if (!cartData) cartData = await fetchCartJson();

        const desiredCode = getHighestQualifyingDiscountCode(effectiveSubtotal);
        const currentCodes = (cartData.discount_codes || []).map(function (d) {
          return d.code;
        });

        const allTierCodes = [];
        tiers.forEach(function (tier) {
          if (tier.rewardType === 'discount_code' && tier.discountCode) {
            allTierCodes.push(tier.discountCode);
          }
        });

        const userCodes = currentCodes.filter(function (code) {
          return !allTierCodes.includes(code);
        });

        const newCodes = userCodes.slice();
        if (desiredCode) {
          newCodes.push(desiredCode);
        }

        const newDiscountString = newCodes.join(',');
        const currentDiscountString = currentCodes.join(',');

        if (newDiscountString !== currentDiscountString) {
          await updateCartDiscount(newDiscountString);
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        }
      }
    } catch (error) {
      console.error('[Tiered Offers] Error:', error);
      isProcessing = false;
    }
  }

  // --- Calculate segmented fill % (equal-width segments) ---
  function calcSegmentedFill(subtotal) {
    if (tiers.length === 0) return 0;
    const segmentWidth = 100 / tiers.length;

    for (let i = tiers.length - 1; i >= 0; i--) {
      if (subtotal >= tiers[i].threshold) {
        if (i === tiers.length - 1) return 100;
        const nextTier = tiers[i + 1];
        const progressInNext =
          (subtotal - tiers[i].threshold) / (nextTier.threshold - tiers[i].threshold);
        return (i + 1) * segmentWidth + progressInNext * segmentWidth;
      }
    }

    // Below first tier
    if (tiers[0].threshold > 0) {
      return (subtotal / tiers[0].threshold) * segmentWidth;
    }
    return 0;
  }

  // --- Update progress bar, segment labels, and message ---
  function updateUI(effectiveSubtotal) {
    if (!tieredOffersEl || tiers.length === 0) return;

    // Fill bar with segmented calculation
    const fillPercent = Math.min(calcSegmentedFill(effectiveSubtotal), 100);
    const fillEl = tieredOffersEl.querySelector('[data-tiered-offers-fill]');
    if (fillEl) {
      fillEl.style.width = fillPercent + '%';
    }

    // Segment labels — update amounts to presentment currency and toggle reached state
    let nextTier = null;
    tiers.forEach(function (tier) {
      const isReached = effectiveSubtotal >= tier.threshold;

      const segmentLabel = tieredOffersEl.querySelector('[data-tier-segment="' + tier.tier + '"]');
      if (segmentLabel) {
        segmentLabel.classList.toggle('is-reached', isReached);
      }

      // Update the threshold amount to converted currency
      const amountEl = tieredOffersEl.querySelector('[data-tier-amount="' + tier.tier + '"]');
      if (amountEl) {
        const convertedAmount = tier.threshold; // already in presentment currency cents
        if (window.theme && window.theme.formatMoney) {
          amountEl.innerHTML = window.theme
            .formatMoney(convertedAmount, window.theme.moneyFormat)
            .replace('.00', '');
        }
      }

      if (!isReached && nextTier === null) {
        nextTier = tier;
      }
    });

    // Status message
    const statusEl = tieredOffersEl.querySelector('[data-tiered-offers-status]');
    if (statusEl) {
      if (nextTier) {
        const amountNeeded = nextTier.threshold - effectiveSubtotal;
        let formatted = '';
        if (window.theme && window.theme.formatMoney) {
          formatted = window.theme
            .formatMoney(amountNeeded, window.theme.moneyFormat)
            .replace('.00', '');
        } else {
          formatted = '$' + (amountNeeded / 100).toFixed(0);
        }
        statusEl.innerHTML =
          'Spend <strong>' +
          formatted +
          '</strong> more to unlock <strong>' +
          nextTier.label +
          '</strong>';
      } else {
        statusEl.innerHTML = 'All rewards unlocked!';
      }
    }
  }

  // --- Enforce disabled state on + buttons for gift items at max qty ---
  function enforceGiftQtyButtons() {
    tiers.forEach(function (tier) {
      if (tier.rewardType !== 'free_product' || !tier.variantId) return;
      const itemEl = document.querySelector('[data-item^="' + tier.variantId + ':"]');
      if (!itemEl) return;

      const input = itemEl.querySelector('.cart__quantity-field');
      if (!input) return;

      const qty = parseInt(input.value, 10) || 0;
      const plusBtn = itemEl.querySelector('.cart__quantity-plus');
      if (plusBtn) {
        if (qty >= tier.maxQty) {
          plusBtn.setAttribute('disabled', '');
        } else {
          plusBtn.removeAttribute('disabled');
        }
      }
    });
  }

  // --- Event listeners and init ---
  function init() {
    if (!readConfig()) return;

    document.addEventListener('theme:cart:change', function () {
      if (!isProcessing) {
        // Re-read config in case the DOM was rebuilt
        readConfig();
        evaluateAndApply(true);
      }
      enforceGiftQtyButtons();
    });

    document.addEventListener('theme:product:added', function () {
      if (!isProcessing) {
        readConfig();
        evaluateAndApply(true);
      }
      enforceGiftQtyButtons();
    });

    // Enforce max quantity on gift items
    document.addEventListener('theme:cart:update', function (e) {
      const id = e.detail && e.detail.id;
      const qty = parseInt(e.detail && e.detail.quantity, 10);
      if (!id || !qty) return;

      for (let i = 0; i < tiers.length; i++) {
        const tier = tiers[i];
        if (
          tier.rewardType === 'free_product' &&
          tier.variantId &&
          id.startsWith(tier.variantId + ':') &&
          qty > tier.maxQty
        ) {
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: id, quantity: tier.maxQty }),
          }).then(function () {
            document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          });
          break;
        }
      }
    });

    // Initial evaluation (page load — do not show error toasts)
    evaluateAndApply(false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Chain Length Fit Guide — injected via JS on chain product pages
 * No modifications to product-variant-picker.liquid needed
 */
(function () {
  'use strict';

  // Only run on product pages
  if (!document.querySelector('.template-product')) return;

  var handle = window.location.pathname.toLowerCase();
  var isChain = handle.includes('chain') || handle.includes('cuban') || handle.includes('rope') || handle.includes('franco') || handle.includes('wheat') || handle.includes('figaro') || handle.includes('curb') || handle.includes('cable') || handle.includes('mariner');
  if (!isChain) return;

  var noPendant = handle.includes('miami-cuban') || handle.includes('mariner');

  // Wait for DOM to be ready and variant picker to initialize
  // Inject once — link placed outside the variant picker's re-render zone
  var chainGuideInjected = false;

  function injectGuide() {
    if (chainGuideInjected) return;

    // Find the variant picker block (parent that doesn't re-render)
    var variantBlock = document.querySelector('.product__block[data-product-block-type="variants"], variant-selects, .product-form__variants');
    if (!variantBlock) return;

    // Verify this product has a Length option
    var hasLength = false;
    var legends = variantBlock.querySelectorAll('.radio__legend, .radio__legend__option-name');
    legends.forEach(function (el) {
      if (el.textContent.trim().toLowerCase().includes('length')) hasLength = true;
    });
    if (!hasLength) return;

    // Create the link button and insert AFTER the variant block
    var link = document.createElement('button');
    link.type = 'button';
    link.className = 'chain-fit-guide-link';
    link.setAttribute('data-open-chain-guide', 'chain-pdp');
    link.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/><path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg> Not sure? See our chain length fit guide';
    variantBlock.parentNode.insertBefore(link, variantBlock.nextSibling);
    chainGuideInjected = true;

    // Create the overlay (appended to body)
    if (document.querySelector('[data-chain-guide-overlay="chain-pdp"]')) return;

    var warningHtml = noPendant
      ? '<div class="chain-fit-guide__warning"><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 9v4m0 4h.01M12 3L2 21h20L12 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>This chain is not compatible with pendants due to its thickness.</div>'
      : '';

    var overlay = document.createElement('div');
    overlay.className = 'chain-fit-guide__overlay';
    overlay.setAttribute('data-chain-guide-overlay', 'chain-pdp');
    overlay.style.display = 'none';
    overlay.innerHTML =
      '<div class="chain-fit-guide__modal">' +
        '<div class="chain-fit-guide__header">' +
          '<h3 class="chain-fit-guide__title">Chain Length Guide</h3>' +
          '<button type="button" class="chain-fit-guide__close" data-close-chain-guide aria-label="Close">' +
            '<svg width="16" height="16" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="chain-fit-guide__body">' +
          '<div class="chain-fit-guide__image"><img src="https://cdn.shopify.com/s/files/1/0873/9742/files/chain-size-chart_480x480.jpg?v=1713340506" alt="Chain length guide" width="480" height="480" loading="lazy"></div>' +
          '<div class="chain-fit-guide__lengths">' +
            '<div class="chain-fit-guide__length-item"><span class="chain-fit-guide__length-size">20&quot;</span><span class="chain-fit-guide__length-desc">Sits at the base of the neck. Tight fit, ideal for layering.</span><span class="chain-fit-guide__length-cm">50.8 cm</span></div>' +
            '<div class="chain-fit-guide__length-item chain-fit-guide__length-item--popular"><span class="chain-fit-guide__length-size">22&quot;</span><span class="chain-fit-guide__length-desc">Falls just below the collarbone. Best length for pendants and everyday wear.</span><span class="chain-fit-guide__length-cm-wrap"><span class="chain-fit-guide__length-badge">Most Popular</span><span class="chain-fit-guide__length-cm">55.88 cm</span></span></div>' +
            '<div class="chain-fit-guide__length-item"><span class="chain-fit-guide__length-size">24&quot;</span><span class="chain-fit-guide__length-desc">Rests a few inches below the collarbone. Relaxed, casual fit.</span><span class="chain-fit-guide__length-cm">60.96 cm</span></div>' +
            '<div class="chain-fit-guide__length-item"><span class="chain-fit-guide__length-size">28&quot;</span><span class="chain-fit-guide__length-desc">Hangs at mid-chest. Bold, statement length.</span><span class="chain-fit-guide__length-cm">71.12 cm</span></div>' +
          '</div>' +
          '<div class="chain-fit-guide__model-note">Model height: 6\'0&quot; / 1.83 m / 183 cm</div>' +
          warningHtml +
          '<div class="chain-fit-guide__tip"><strong>Tip:</strong> If you\'re between sizes, go with the longer option. You can always layer, but you can\'t add length.</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);
  }

  // Run after a delay to ensure theme JS has fully initialized
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(injectGuide, 500); });
  } else {
    setTimeout(injectGuide, 500);
  }


  // Event delegation for open/close
  function closeAllGuides() {
    document.querySelectorAll('[data-chain-guide-overlay]').forEach(function (ol) {
      ol.style.display = 'none';
    });
    document.body.style.overflow = '';
  }

  // Click/keydown handlers removed — handled by consolidated global handler below
})();

/**
 * Ring Size Guide — injected via JS on ring product pages
 */
(function () {
  'use strict';

  if (!document.querySelector('.template-product')) return;

  var handle = window.location.pathname.toLowerCase();
  var isRing = handle.includes('ring') || handle.includes('band') || handle.includes('signet');
  if (!isRing) return;
  // Don't show on chain pages
  if (handle.includes('chain')) return;

  var ringGuideInjected = false;

  function injectGuide() {
    if (ringGuideInjected) return;

    var variantBlock = document.querySelector('.product__block[data-product-block-type="variants"], variant-selects, .product-form__variants');
    if (!variantBlock) return;

    // Verify this product has a Size option
    var hasSize = false;
    var legends = variantBlock.querySelectorAll('.radio__legend, .radio__legend__option-name');
    legends.forEach(function (el) {
      var text = el.textContent.trim().toLowerCase();
      if (text.includes('size') || text.includes('ring')) hasSize = true;
    });
    if (!hasSize) return;

    var link = document.createElement('button');
    link.type = 'button';
    link.className = 'chain-fit-guide-link ring-fit-guide-link';
    link.setAttribute('data-open-ring-guide', 'ring-pdp');
    link.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/><path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg> Not sure? See our ring size guide';
    variantBlock.parentNode.insertBefore(link, variantBlock.nextSibling);
    ringGuideInjected = true;

    if (document.querySelector('[data-ring-guide-overlay="ring-pdp"]')) return;

    var overlay = document.createElement('div');
    overlay.className = 'chain-fit-guide__overlay';
    overlay.setAttribute('data-chain-guide-overlay', 'ring-pdp');
    overlay.setAttribute('data-ring-guide-overlay', 'ring-pdp');
    overlay.style.display = 'none';
    overlay.innerHTML =
      '<div class="chain-fit-guide__modal">' +
        '<div class="chain-fit-guide__header">' +
          '<h3 class="chain-fit-guide__title">Ring Size Guide</h3>' +
          '<button type="button" class="chain-fit-guide__close" data-close-ring-guide aria-label="Close">' +
            '<svg width="16" height="16" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="chain-fit-guide__body">' +
          '<div class="ring-guide__tabs">' +
            '<button type="button" class="ring-guide__tab ring-guide__tab--active" data-ring-tab="existing">Measure an Existing Ring</button>' +
            '<button type="button" class="ring-guide__tab" data-ring-tab="finger">Measure Your Finger</button>' +
          '</div>' +

          '<div class="ring-guide__panel" data-ring-panel="existing">' +
            '<div class="ring-guide__instructions">' +
              '<p>Use a ring that already fits the finger you\'re sizing for.</p>' +
              '<p>Place it flat and measure the <strong>internal diameter</strong> in millimetres. Make sure your ruler starts at exactly the 0mm mark.</p>' +
            '</div>' +
            '<div class="ring-guide__diagram">' +
              '<svg viewBox="0 0 200 200" width="140" height="140" xmlns="http://www.w3.org/2000/svg">' +
                '<circle cx="100" cy="95" r="80" fill="none" stroke="#1a1a1a" stroke-width="5"/>' +
                '<circle cx="100" cy="95" r="55" fill="none" stroke="#1a1a1a" stroke-width="4"/>' +
                '<line x1="45" y1="95" x2="155" y2="95" stroke="#c9a96e" stroke-width="2"/>' +
                '<polygon points="45,95 52,91 52,99" fill="#c9a96e"/>' +
                '<polygon points="155,95 148,91 148,99" fill="#c9a96e"/>' +
                '<text x="100" y="118" text-anchor="middle" font-size="11" font-weight="500" letter-spacing="0.15em" fill="#1a1a1a" font-family="sans-serif">DIAMETER</text>' +
              '</svg>' +
            '</div>' +
            '<div class="ring-guide__instructions"><p>Find your measurement in the chart below.</p></div>' +
            '<table class="ring-guide__table">' +
              '<thead><tr>' +
                '<th>Diameter (mm)</th><th>US / CAN</th><th>UK / AUS</th><th>German</th>' +
              '</tr></thead>' +
              '<tbody>' +
                '<tr><td>16.5 mm</td><td>6</td><td>N</td><td>16\u00BD</td></tr>' +
                '<tr><td>17.3 mm</td><td>7</td><td>P</td><td>17\u00BC</td></tr>' +
                '<tr><td>18.1 mm</td><td>8</td><td>R</td><td>18</td></tr>' +
                '<tr><td>19.0 mm</td><td>9</td><td>T</td><td>19</td></tr>' +
                '<tr><td>19.8 mm</td><td>10</td><td>V</td><td>20</td></tr>' +
                '<tr><td>20.6 mm</td><td>11</td><td>F</td><td>20\u00BE</td></tr>' +
                '<tr><td>21.4 mm</td><td>12</td><td>X</td><td>21\u00BC</td></tr>' +
                '<tr><td>22.2 mm</td><td>13</td><td>Z</td><td>22</td></tr>' +
              '</tbody>' +
            '</table>' +
          '</div>' +

          '<div class="ring-guide__panel" data-ring-panel="finger" style="display:none;">' +
            '<div class="ring-guide__steps">' +
              '<div class="ring-guide__step">' +
                '<div class="ring-guide__step-number">1</div>' +
                '<div class="ring-guide__step-content">' +
                  '<h4>Cut a Strip of Paper</h4>' +
                  '<p>Cut a thin strip of paper roughly 15cm long and about 1cm wide.</p>' +
                  '<img class="ring-guide__step-img" src="https://cdn.shopify.com/s/files/1/0873/9742/files/6_b8d04b07-b207-45f7-ba48-1814a657bee0_480x480.jpg?v=1717696567" alt="Cut a strip of paper" width="120" height="120" loading="lazy">' +
                '</div>' +
              '</div>' +
              '<div class="ring-guide__step">' +
                '<div class="ring-guide__step-number">2</div>' +
                '<div class="ring-guide__step-content">' +
                  '<h4>Wrap It Around Your Finger</h4>' +
                  '<p>Wrap the strip around the finger you want to size, right where the ring would sit. Pull it snug for the most accurate result.</p>' +
                  '<img class="ring-guide__step-img" src="https://cdn.shopify.com/s/files/1/0873/9742/files/8_bd597b73-38d9-4fd6-83a0-cfe8676fe2f1_480x480.jpg?v=1717696567" alt="Wrap around finger" width="120" height="120" loading="lazy">' +
                '</div>' +
              '</div>' +
              '<div class="ring-guide__step">' +
                '<div class="ring-guide__step-number">3</div>' +
                '<div class="ring-guide__step-content">' +
                  '<h4>Mark and Measure</h4>' +
                  '<p>Mark where the paper meets, lay flat on a ruler, and record the length in millimetres. That\'s your circumference.</p>' +
                  '<img class="ring-guide__step-img" src="https://cdn.shopify.com/s/files/1/0873/9742/files/7_f989e816-c14b-47f8-b3c3-db5f4659bf6c_480x480.jpg?v=1717696567" alt="Mark and measure" width="120" height="120" loading="lazy">' +
                '</div>' +
              '</div>' +
            '</div>' +
            '<table class="ring-guide__table">' +
              '<thead><tr>' +
                '<th>Circumference (mm)</th><th>US / CAN</th>' +
              '</tr></thead>' +
              '<tbody>' +
                '<tr><td>51.9 mm</td><td>6</td></tr>' +
                '<tr><td>54.4 mm</td><td>7</td></tr>' +
                '<tr><td>57.0 mm</td><td>8</td></tr>' +
                '<tr><td>59.5 mm</td><td>9</td></tr>' +
                '<tr><td>62.1 mm</td><td>10</td></tr>' +
                '<tr><td>64.6 mm</td><td>11</td></tr>' +
                '<tr><td>67.2 mm</td><td>12</td></tr>' +
                '<tr><td>67.7 mm</td><td>13</td></tr>' +
              '</tbody>' +
            '</table>' +
          '</div>' +

          '<div class="chain-fit-guide__tip"><strong>Tip:</strong> Measure at the end of the day when fingers are at their largest. If you\'re between sizes, go with the larger size.</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);

    // Tab switching
    overlay.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-ring-tab]');
      if (!tab) return;
      var tabName = tab.getAttribute('data-ring-tab');
      overlay.querySelectorAll('.ring-guide__tab').forEach(function (t) {
        t.classList.toggle('ring-guide__tab--active', t.getAttribute('data-ring-tab') === tabName);
      });
      overlay.querySelectorAll('.ring-guide__panel').forEach(function (p) {
        p.style.display = p.getAttribute('data-ring-panel') === tabName ? '' : 'none';
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(injectGuide, 500); });
  } else {
    setTimeout(injectGuide, 500);
  }


  // Event delegation
  function closeRingGuide() {
    document.querySelectorAll('[data-ring-guide-overlay]').forEach(function (ol) {
      ol.style.display = 'none';
    });
    document.body.style.overflow = '';
  }

  // Click/keydown handlers removed — handled by consolidated global handler below
})();

/**
 * Bracelet Size Guide — injected via JS on bracelet product pages
 */
(function () {
  'use strict';

  if (!document.querySelector('.template-product')) return;

  var handle = window.location.pathname.toLowerCase();
  var isBracelet = handle.includes('bracelet') || handle.includes('cuff') || handle.includes('bangle');
  if (!isBracelet) return;
  if (handle.includes('chain') || handle.includes('ring')) return;

  var braceletGuideInjected = false;

  function injectGuide() {
    if (braceletGuideInjected) return;

    var variantBlock = document.querySelector('.product__block[data-product-block-type="variants"], variant-selects, .product-form__variants');
    if (!variantBlock) return;

    var hasSize = false;
    var legends = variantBlock.querySelectorAll('.radio__legend, .radio__legend__option-name');
    legends.forEach(function (el) {
      var text = el.textContent.trim().toLowerCase();
      if (text.includes('size') || text.includes('length')) hasSize = true;
    });
    if (!hasSize) return;

    var link = document.createElement('button');
    link.type = 'button';
    link.className = 'chain-fit-guide-link bracelet-fit-guide-link';
    link.setAttribute('data-open-bracelet-guide', 'bracelet-pdp');
    link.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/><path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg> Not sure? See our bracelet size guide';
    variantBlock.parentNode.insertBefore(link, variantBlock.nextSibling);
    braceletGuideInjected = true;

    if (document.querySelector('[data-bracelet-guide-overlay="bracelet-pdp"]')) return;
    createBraceletOverlay();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(injectGuide, 500); });
  } else {
    setTimeout(injectGuide, 500);
  }

  function createBraceletOverlay() {
    if (document.querySelector('[data-bracelet-guide-overlay="bracelet-pdp"]')) return;
    var sizeTable =
      '<table class="ring-guide__table"><thead><tr>' +
        '<th>Bracelet Size (in)</th><th>Wrist - Inches (in)</th><th>Wrist - Millimeters (mm)</th>' +
      '</tr></thead><tbody>' +
        '<tr><td>7&quot; <span class="ring-guide__size-label">Small</span></td><td>6.1&quot; - 6.5&quot;</td><td>153 - 165 mm</td></tr>' +
        '<tr><td>8&quot; <span class="ring-guide__size-label">Medium</span></td><td>7.1&quot; - 7.5&quot;</td><td>166 - 178 mm</td></tr>' +
        '<tr><td>9&quot; <span class="ring-guide__size-label">Large</span></td><td>8.1&quot; - 8.5&quot;</td><td>205 - 217 mm</td></tr>' +
      '</tbody></table>';

    var overlay = document.createElement('div');
    overlay.className = 'chain-fit-guide__overlay';
    overlay.setAttribute('data-chain-guide-overlay', 'bracelet-pdp');
    overlay.setAttribute('data-bracelet-guide-overlay', 'bracelet-pdp');
    overlay.style.display = 'none';
    overlay.innerHTML =
      '<div class="chain-fit-guide__modal">' +
        '<div class="chain-fit-guide__header">' +
          '<h3 class="chain-fit-guide__title">Bracelet Size Guide</h3>' +
          '<button type="button" class="chain-fit-guide__close" data-close-bracelet-guide aria-label="Close">' +
            '<svg width="16" height="16" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="chain-fit-guide__body">' +
          sizeTable +

          '<div class="bracelet-guide__measure-section">' +
            '<h4 class="bracelet-guide__measure-title">How to Measure Your Wrist</h4>' +

            '<div class="ring-guide__steps">' +
              '<div class="ring-guide__step-heading">Option 1 — Measuring Tape</div>' +
            '</div>' +
            '<div class="chain-fit-guide__image"><img src="https://cdn.shopify.com/s/files/1/0873/9742/files/braceletsizeguidebanner_600x600.jpg?v=1717695535" alt="Measuring tape method" width="600" height="600" loading="lazy"></div>' +
            '<div class="ring-guide__steps">' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">1</div><div class="ring-guide__step-content"><p>Wrap the measuring tape around your wrist just above the wrist bone with your palm open and facing up.</p></div></div>' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">2</div><div class="ring-guide__step-content"><p>Note down the measurement of your wrist.</p></div></div>' +
            '</div>' +

            '<div class="ring-guide__steps" style="margin-top:14px;">' +
              '<div class="ring-guide__step-heading">Option 2 — String Method</div>' +
            '</div>' +
            '<div class="chain-fit-guide__image"><img src="https://cdn.shopify.com/s/files/1/0873/9742/files/braceletropesize_600x600.jpg?v=1717695560" alt="String method" width="600" height="600" loading="lazy"></div>' +
            '<div class="ring-guide__steps">' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">1</div><div class="ring-guide__step-content"><p>Wrap the string around your wrist just above the wrist bone with your palm open and facing up.</p></div></div>' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">2</div><div class="ring-guide__step-content"><p>Mark where the ends meet with a pen or marker.</p></div></div>' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">3</div><div class="ring-guide__step-content"><p>Lay the string flat and measure it to where you\'ve made your mark. Note down the measurement to find your wrist size.</p></div></div>' +
            '</div>' +
          '</div>' +

          '<div class="chain-fit-guide__tip"><strong>Tip:</strong> Measure your wrist at the end of the day when it\'s at its largest. If you\'re between sizes, go with the larger size for a comfortable fit.</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);
  }

  // Event delegation
  function closeBraceletGuide() {
    document.querySelectorAll('[data-bracelet-guide-overlay]').forEach(function (ol) {
      ol.style.display = 'none';
    });
    document.body.style.overflow = '';
  }

  // Click/keydown handlers removed — handled by consolidated global handler below
})();

/**
 * Size Guides in Quick-Add Modals (collection pages)
 * Watches for quick-add dialogs opening and injects chain/ring guides
 */
(function () {
  'use strict';

  function getProductType(dialog) {
    // Check the product title/handle loaded inside the modal
    var title = dialog.querySelector('.product__title, h1, h2');
    var titleText = title ? title.textContent.toLowerCase() : '';
    var handle = '';
    var link = dialog.querySelector('a[href*="/products/"]');
    if (link) handle = link.getAttribute('href').toLowerCase();

    // Check bracelet FIRST (products like "franco-bracelet" contain chain keywords too)
    if (handle.includes('bracelet') || handle.includes('cuff') || handle.includes('bangle') ||
        titleText.includes('bracelet')) {
      return 'bracelet';
    }
    if (handle.includes('chain') || handle.includes('cuban') || handle.includes('rope') ||
        handle.includes('franco') || handle.includes('wheat') || handle.includes('figaro') ||
        handle.includes('curb') || handle.includes('cable') || handle.includes('mariner') ||
        titleText.includes('chain')) {
      return 'chain';
    }
    // Check ring last (avoid matching "ring" in "earring")
    if ((handle.includes('ring') && !handle.includes('earring')) || handle.includes('signet') ||
        (titleText.includes('ring') && !titleText.includes('earring'))) {
      return 'ring';
    }
    return null;
  }

  function hasLengthOrSizeOption(dialog) {
    var legends = dialog.querySelectorAll('.radio__legend, .radio__legend__option-name');
    var result = { hasLength: false, hasSize: false };
    legends.forEach(function (el) {
      var text = el.textContent.trim().toLowerCase();
      if (text.includes('length')) result.hasLength = true;
      if (text.includes('size')) result.hasSize = true;
    });
    return result;
  }

  function injectQuickAddGuide(dialog) {
    if (dialog.querySelector('.chain-fit-guide-link, .ring-fit-guide-link, .bracelet-fit-guide-link')) return;

    var type = getProductType(dialog);
    if (!type) return;

    var options = hasLengthOrSizeOption(dialog);

    var variantBlock = dialog.querySelector('variant-selects, .product-form__variants, .product__form');
    if (!variantBlock) return;

    if (type === 'chain' && options.hasLength) {
      var link = document.createElement('button');
      link.type = 'button';
      link.className = 'chain-fit-guide-link';
      link.setAttribute('data-open-chain-guide', 'chain-pdp');
      link.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/><path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg> Not sure? See our chain length fit guide';
      variantBlock.parentNode.insertBefore(link, variantBlock.nextSibling);

      // Ensure the chain overlay exists on body
      if (!document.querySelector('[data-chain-guide-overlay="chain-pdp"]')) {
        // It should already exist if on a chain collection page, but just in case
        createChainOverlay();
      }
    }

    if (type === 'bracelet' && (options.hasSize || options.hasLength)) {
      var bLink = document.createElement('button');
      bLink.type = 'button';
      bLink.className = 'chain-fit-guide-link bracelet-fit-guide-link';
      bLink.setAttribute('data-open-bracelet-guide', 'bracelet-pdp');
      bLink.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/><path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg> Not sure? See our bracelet size guide';
      variantBlock.parentNode.insertBefore(bLink, variantBlock.nextSibling);

      if (!document.querySelector('[data-bracelet-guide-overlay="bracelet-pdp"]')) {
        createBraceletOverlayGlobal();
      }
    }

    if (type === 'ring' && options.hasSize) {
      var rLink = document.createElement('button');
      rLink.type = 'button';
      rLink.className = 'chain-fit-guide-link ring-fit-guide-link';
      rLink.setAttribute('data-open-ring-guide', 'ring-pdp');
      rLink.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/><path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg> Not sure? See our ring size guide';
      variantBlock.parentNode.insertBefore(rLink, variantBlock.nextSibling);

      if (!document.querySelector('[data-ring-guide-overlay="ring-pdp"]')) {
        createRingOverlay();
      }
    }
  }

  function createChainOverlay() {
    var handle = window.location.pathname.toLowerCase();
    var noPendant = handle.includes('miami-cuban') || handle.includes('mariner');
    var warningHtml = noPendant
      ? '<div class="chain-fit-guide__warning"><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 9v4m0 4h.01M12 3L2 21h20L12 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>This chain is not compatible with pendants due to its thickness.</div>'
      : '';
    var overlay = document.createElement('div');
    overlay.className = 'chain-fit-guide__overlay';
    overlay.setAttribute('data-chain-guide-overlay', 'chain-pdp');
    overlay.style.display = 'none';
    overlay.innerHTML =
      '<div class="chain-fit-guide__modal">' +
        '<div class="chain-fit-guide__header">' +
          '<h3 class="chain-fit-guide__title">Chain Length Guide</h3>' +
          '<button type="button" class="chain-fit-guide__close" data-close-chain-guide aria-label="Close">' +
            '<svg width="16" height="16" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="chain-fit-guide__body">' +
          '<div class="chain-fit-guide__image"><img src="https://cdn.shopify.com/s/files/1/0873/9742/files/chain-size-chart_480x480.jpg?v=1713340506" alt="Chain length guide" width="480" height="480" loading="lazy"></div>' +
          '<div class="chain-fit-guide__lengths">' +
            '<div class="chain-fit-guide__length-item"><span class="chain-fit-guide__length-size">20&quot;</span><span class="chain-fit-guide__length-desc">Sits at the base of the neck. Tight fit, ideal for layering.</span><span class="chain-fit-guide__length-cm">50.8 cm</span></div>' +
            '<div class="chain-fit-guide__length-item chain-fit-guide__length-item--popular"><span class="chain-fit-guide__length-size">22&quot;</span><span class="chain-fit-guide__length-desc">Falls just below the collarbone. Best length for pendants and everyday wear.</span><span class="chain-fit-guide__length-cm-wrap"><span class="chain-fit-guide__length-badge">Most Popular</span><span class="chain-fit-guide__length-cm">55.88 cm</span></span></div>' +
            '<div class="chain-fit-guide__length-item"><span class="chain-fit-guide__length-size">24&quot;</span><span class="chain-fit-guide__length-desc">Rests a few inches below the collarbone. Relaxed, casual fit.</span><span class="chain-fit-guide__length-cm">60.96 cm</span></div>' +
            '<div class="chain-fit-guide__length-item"><span class="chain-fit-guide__length-size">28&quot;</span><span class="chain-fit-guide__length-desc">Hangs at mid-chest. Bold, statement length.</span><span class="chain-fit-guide__length-cm">71.12 cm</span></div>' +
          '</div>' +
          '<div class="chain-fit-guide__model-note">Model height: 6\'0&quot; / 1.83 m / 183 cm</div>' +
          warningHtml +
          '<div class="chain-fit-guide__tip"><strong>Tip:</strong> If you\'re between sizes, go with the longer option. You can always layer, but you can\'t add length.</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);
  }

  function createBraceletOverlayGlobal() {
    if (document.querySelector('[data-bracelet-guide-overlay="bracelet-pdp"]')) return;
    var sizeTable =
      '<table class="ring-guide__table"><thead><tr>' +
        '<th>Bracelet Size (in)</th><th>Wrist - Inches (in)</th><th>Wrist - Millimeters (mm)</th>' +
      '</tr></thead><tbody>' +
        '<tr><td>7&quot; <span class="ring-guide__size-label">Small</span></td><td>6.1&quot; - 6.5&quot;</td><td>153 - 165 mm</td></tr>' +
        '<tr><td>8&quot; <span class="ring-guide__size-label">Medium</span></td><td>7.1&quot; - 7.5&quot;</td><td>166 - 178 mm</td></tr>' +
        '<tr><td>9&quot; <span class="ring-guide__size-label">Large</span></td><td>8.1&quot; - 8.5&quot;</td><td>205 - 217 mm</td></tr>' +
      '</tbody></table>';

    var overlay = document.createElement('div');
    overlay.className = 'chain-fit-guide__overlay';
    overlay.setAttribute('data-chain-guide-overlay', 'bracelet-pdp');
    overlay.setAttribute('data-bracelet-guide-overlay', 'bracelet-pdp');
    overlay.style.display = 'none';
    overlay.innerHTML =
      '<div class="chain-fit-guide__modal">' +
        '<div class="chain-fit-guide__header">' +
          '<h3 class="chain-fit-guide__title">Bracelet Size Guide</h3>' +
          '<button type="button" class="chain-fit-guide__close" data-close-bracelet-guide aria-label="Close">' +
            '<svg width="16" height="16" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="chain-fit-guide__body">' +
          sizeTable +
          '<div class="bracelet-guide__measure-section">' +
            '<h4 class="bracelet-guide__measure-title">How to Measure Your Wrist</h4>' +

            '<div class="ring-guide__steps">' +
              '<div class="ring-guide__step-heading">Option 1 — Measuring Tape</div>' +
            '</div>' +
            '<div class="chain-fit-guide__image"><img src="https://cdn.shopify.com/s/files/1/0873/9742/files/braceletsizeguidebanner_600x600.jpg?v=1717695535" alt="Measuring tape method" width="600" height="600" loading="lazy"></div>' +
            '<div class="ring-guide__steps">' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">1</div><div class="ring-guide__step-content"><p>Wrap the measuring tape around your wrist just above the wrist bone with your palm open and facing up.</p></div></div>' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">2</div><div class="ring-guide__step-content"><p>Note down the measurement of your wrist.</p></div></div>' +
            '</div>' +

            '<div class="ring-guide__steps" style="margin-top:14px;">' +
              '<div class="ring-guide__step-heading">Option 2 — String Method</div>' +
            '</div>' +
            '<div class="chain-fit-guide__image"><img src="https://cdn.shopify.com/s/files/1/0873/9742/files/braceletropesize_600x600.jpg?v=1717695560" alt="String method" width="600" height="600" loading="lazy"></div>' +
            '<div class="ring-guide__steps">' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">1</div><div class="ring-guide__step-content"><p>Wrap the string around your wrist just above the wrist bone with your palm open and facing up.</p></div></div>' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">2</div><div class="ring-guide__step-content"><p>Mark where the ends meet with a pen or marker.</p></div></div>' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">3</div><div class="ring-guide__step-content"><p>Lay the string flat and measure it to where you\'ve made your mark. Note down the measurement to find your wrist size.</p></div></div>' +
            '</div>' +
          '</div>' +
          '<div class="chain-fit-guide__tip"><strong>Tip:</strong> Measure your wrist at the end of the day when it\'s at its largest. If you\'re between sizes, go with the larger size for a comfortable fit.</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);
  }

  function createRingOverlay() {
    var overlay = document.createElement('div');
    overlay.className = 'chain-fit-guide__overlay';
    overlay.setAttribute('data-chain-guide-overlay', 'ring-pdp');
    overlay.setAttribute('data-ring-guide-overlay', 'ring-pdp');
    overlay.style.display = 'none';
    overlay.innerHTML =
      '<div class="chain-fit-guide__modal">' +
        '<div class="chain-fit-guide__header">' +
          '<h3 class="chain-fit-guide__title">Ring Size Guide</h3>' +
          '<button type="button" class="chain-fit-guide__close" data-close-ring-guide aria-label="Close">' +
            '<svg width="16" height="16" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="chain-fit-guide__body">' +
          '<div class="ring-guide__tabs">' +
            '<button type="button" class="ring-guide__tab ring-guide__tab--active" data-ring-tab="existing">Measure an Existing Ring</button>' +
            '<button type="button" class="ring-guide__tab" data-ring-tab="finger">Measure Your Finger</button>' +
          '</div>' +
          '<div class="ring-guide__panel" data-ring-panel="existing">' +
            '<div class="ring-guide__instructions">' +
              '<p>Use a ring that already fits the finger you\'re sizing for.</p>' +
              '<p>Place it flat and measure the <strong>internal diameter</strong> in millimetres. Make sure your ruler starts at exactly the 0mm mark.</p>' +
            '</div>' +
            '<div class="ring-guide__diagram">' +
              '<svg viewBox="0 0 200 200" width="140" height="140" xmlns="http://www.w3.org/2000/svg">' +
                '<circle cx="100" cy="95" r="80" fill="none" stroke="#1a1a1a" stroke-width="5"/>' +
                '<circle cx="100" cy="95" r="55" fill="none" stroke="#1a1a1a" stroke-width="4"/>' +
                '<line x1="45" y1="95" x2="155" y2="95" stroke="#c9a96e" stroke-width="2"/>' +
                '<polygon points="45,95 52,91 52,99" fill="#c9a96e"/>' +
                '<polygon points="155,95 148,91 148,99" fill="#c9a96e"/>' +
                '<text x="100" y="118" text-anchor="middle" font-size="11" font-weight="500" letter-spacing="0.15em" fill="#1a1a1a" font-family="sans-serif">DIAMETER</text>' +
              '</svg>' +
            '</div>' +
            '<div class="ring-guide__instructions"><p>Find your measurement in the chart below.</p></div>' +
            '<table class="ring-guide__table"><thead><tr><th>Diameter (mm)</th><th>US / CAN</th><th>UK / AUS</th><th>German</th></tr></thead><tbody>' +
              '<tr><td>16.5 mm</td><td>6</td><td>N</td><td>16\u00BD</td></tr>' +
              '<tr><td>17.3 mm</td><td>7</td><td>P</td><td>17\u00BC</td></tr>' +
              '<tr><td>18.1 mm</td><td>8</td><td>R</td><td>18</td></tr>' +
              '<tr><td>19.0 mm</td><td>9</td><td>T</td><td>19</td></tr>' +
              '<tr><td>19.8 mm</td><td>10</td><td>V</td><td>20</td></tr>' +
              '<tr><td>20.6 mm</td><td>11</td><td>F</td><td>20\u00BE</td></tr>' +
              '<tr><td>21.4 mm</td><td>12</td><td>X</td><td>21\u00BC</td></tr>' +
              '<tr><td>22.2 mm</td><td>13</td><td>Z</td><td>22</td></tr>' +
            '</tbody></table>' +
          '</div>' +
          '<div class="ring-guide__panel" data-ring-panel="finger" style="display:none;">' +
            '<div class="ring-guide__steps">' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">1</div><div class="ring-guide__step-content"><h4>Cut a Strip of Paper</h4><p>Cut a thin strip of paper roughly 15cm long and about 1cm wide.</p><img class="ring-guide__step-img" src="https://cdn.shopify.com/s/files/1/0873/9742/files/6_b8d04b07-b207-45f7-ba48-1814a657bee0_480x480.jpg?v=1717696567" alt="Cut paper" width="120" height="120" loading="lazy"></div></div>' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">2</div><div class="ring-guide__step-content"><h4>Wrap It Around Your Finger</h4><p>Wrap the strip around the finger you want to size, right where the ring would sit. Pull it snug.</p><img class="ring-guide__step-img" src="https://cdn.shopify.com/s/files/1/0873/9742/files/8_bd597b73-38d9-4fd6-83a0-cfe8676fe2f1_480x480.jpg?v=1717696567" alt="Wrap finger" width="120" height="120" loading="lazy"></div></div>' +
              '<div class="ring-guide__step"><div class="ring-guide__step-number">3</div><div class="ring-guide__step-content"><h4>Mark and Measure</h4><p>Mark where the paper meets, lay flat on a ruler, and record the length in millimetres.</p><img class="ring-guide__step-img" src="https://cdn.shopify.com/s/files/1/0873/9742/files/7_f989e816-c14b-47f8-b3c3-db5f4659bf6c_480x480.jpg?v=1717696567" alt="Mark measure" width="120" height="120" loading="lazy"></div></div>' +
            '</div>' +
            '<table class="ring-guide__table"><thead><tr><th>Circumference (mm)</th><th>US / CAN</th></tr></thead><tbody>' +
              '<tr><td>51.9 mm</td><td>6</td></tr><tr><td>54.4 mm</td><td>7</td></tr><tr><td>57.0 mm</td><td>8</td></tr><tr><td>59.5 mm</td><td>9</td></tr><tr><td>62.1 mm</td><td>10</td></tr><tr><td>64.6 mm</td><td>11</td></tr><tr><td>67.2 mm</td><td>12</td></tr><tr><td>67.7 mm</td><td>13</td></tr>' +
            '</tbody></table>' +
          '</div>' +
          '<div class="chain-fit-guide__tip"><strong>Tip:</strong> Measure at the end of the day when fingers are at their largest. If you\'re between sizes, go with the larger size.</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);

    // Tab switching
    overlay.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-ring-tab]');
      if (!tab) return;
      var tabName = tab.getAttribute('data-ring-tab');
      overlay.querySelectorAll('.ring-guide__tab').forEach(function (t) {
        t.classList.toggle('ring-guide__tab--active', t.getAttribute('data-ring-tab') === tabName);
      });
      overlay.querySelectorAll('.ring-guide__panel').forEach(function (p) {
        p.style.display = p.getAttribute('data-ring-panel') === tabName ? '' : 'none';
      });
    });
  }

  // Watch for quick-add modals being populated with AJAX content
  var quickAddObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        // Check if a quick-add dialog got new content
        var dialog = node.closest ? node.closest('dialog.product-quick-add') : null;
        if (dialog) {
          setTimeout(function () { injectQuickAddGuide(dialog); }, 300);
        }
        // Also check if the node itself is a dialog
        if (node.matches && node.matches('dialog.product-quick-add')) {
          setTimeout(function () { injectQuickAddGuide(node); }, 300);
        }
      });
    });
  });
  quickAddObserver.observe(document.body, { childList: true, subtree: true });

  // Also listen for the theme's quick-add open event
  document.addEventListener('theme:quick-add:open', function () {
    setTimeout(function () {
      var openDialog = document.querySelector('dialog.product-quick-add[open]');
      if (openDialog) injectQuickAddGuide(openDialog);
    }, 500);
  });

  // Quick-add click fallback removed — MutationObserver + theme event already handle this

  // Global event delegation for opening/closing guides (works on all pages)
  function closeAllGuides() {
    document.querySelectorAll('[data-chain-guide-overlay], [data-ring-guide-overlay], [data-bracelet-guide-overlay]').forEach(function (ol) {
      ol.style.display = 'none';
    });
    document.body.style.overflow = '';
  }

  document.addEventListener('click', function (e) {
    // Open chain guide
    var openChain = e.target.closest('[data-open-chain-guide]');
    if (openChain) {
      var id = openChain.getAttribute('data-open-chain-guide');
      var ol = document.querySelector('[data-chain-guide-overlay="' + id + '"]');
      if (!ol) { createChainOverlay(); ol = document.querySelector('[data-chain-guide-overlay="' + id + '"]'); }
      if (ol) { ol.style.display = 'flex'; ol.scrollTop = 0; document.body.style.overflow = 'hidden'; }
      return;
    }
    // Open bracelet guide
    var openBracelet = e.target.closest('[data-open-bracelet-guide]');
    if (openBracelet) {
      var ol = document.querySelector('[data-bracelet-guide-overlay="bracelet-pdp"]');
      if (!ol) { createBraceletOverlayGlobal(); ol = document.querySelector('[data-bracelet-guide-overlay="bracelet-pdp"]'); }
      if (ol) { ol.style.display = 'flex'; ol.scrollTop = 0; document.body.style.overflow = 'hidden'; }
      return;
    }
    // Open ring guide
    var openRing = e.target.closest('[data-open-ring-guide]');
    if (openRing) {
      var ol = document.querySelector('[data-ring-guide-overlay="ring-pdp"]');
      if (!ol) { createRingOverlay(); ol = document.querySelector('[data-ring-guide-overlay="ring-pdp"]'); }
      if (ol) { ol.style.display = 'flex'; ol.scrollTop = 0; document.body.style.overflow = 'hidden'; }
      return;
    }
    // Close buttons
    var closeBtn = e.target.closest('[data-close-chain-guide], [data-close-ring-guide], [data-close-bracelet-guide]');
    if (closeBtn) { closeAllGuides(); return; }
    // Close on overlay background click
    if (e.target.hasAttribute && (e.target.hasAttribute('data-chain-guide-overlay') || e.target.hasAttribute('data-ring-guide-overlay') || e.target.hasAttribute('data-bracelet-guide-overlay'))) {
      closeAllGuides();
    }

    // Checkout flag (merged from separate handler — INP optimization)
    var checkoutEl = e.target.closest('a[href*="/checkout"], button[name="checkout"], [data-cart-checkout-button], input[name="checkout"]');
    if (checkoutEl) {
      sessionStorage.setItem('marcozo_went_to_checkout', '1');
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllGuides();
  });

  // Catch form submissions to /checkout
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (form.action && form.action.indexOf('/checkout') !== -1) {
      sessionStorage.setItem('marcozo_went_to_checkout', '1');
    }
  });
})();

/**
 * Force page reload only when returning from checkout
 * Sets a flag when navigating to checkout, reloads on back if flag exists
 */
(function () {
  'use strict';

  var FLAG = 'marcozo_went_to_checkout';

  // On page load/restore: if flag exists, reload and clear it
  function checkAndReload() {
    if (sessionStorage.getItem(FLAG)) {
      sessionStorage.removeItem(FLAG);
      window.location.reload();
    }
  }

  // Safari bfcache
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      checkAndReload();
    }
  });

  // Chrome/Firefox back_forward
  var navEntries = window.performance && window.performance.getEntriesByType('navigation');
  if (navEntries && navEntries.length > 0 && navEntries[0].type === 'back_forward') {
    checkAndReload();
  }
})();
